import React, { forwardRef, useRef, useState, useMemo, useEffect } from 'react';
import applyDevTools from 'prosemirror-dev-tools';
import { NodeSelection, TextSelection, PluginKey, Plugin, Selection, EditorState } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { history } from 'prosemirror-history';
import { keymap } from 'prosemirror-keymap';
import { baseKeymap, toggleMark } from 'prosemirror-commands';
import { canSplit } from 'prosemirror-transform';
import { Fragment, Node, Schema } from 'prosemirror-model';
import styled, { css, ThemeProvider } from 'styled-components';
import { findSelectedNodeOfType, findParentNodeOfType, safeInsert } from 'prosemirror-utils';
import Fuse from 'fuse.js';
import PropTypes from 'prop-types';
import { InputRule, inputRules } from 'prosemirror-inputrules';
import rafSchedule from 'raf-schd';
import { createPortal, unstable_renderSubtreeIntoContainer, unmountComponentAtNode } from 'react-dom';
import DevIcon from '@atlaskit/icon/glyph/editor/code';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
    return extendStatics(d, b);
};

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

function __spreadArrays() {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
}
function __makeTemplateObject(cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
}

// https://bitbucket.org/atlassian/atlassian-frontend-mirror/src/master/editor/editor-core/src/event-dispatcher/index.ts
var EventDispatcher = /** @class */ (function () {
    function EventDispatcher() {
        this.listeners = {};
    }
    EventDispatcher.prototype.on = function (event, cb) {
        if (!this.listeners[event]) {
            this.listeners[event] = new Set();
        }
        this.listeners[event].add(cb);
    };
    EventDispatcher.prototype.off = function (event, cb) {
        if (!this.listeners[event]) {
            return;
        }
        if (this.listeners[event].has(cb)) {
            this.listeners[event].delete(cb);
        }
    };
    EventDispatcher.prototype.emit = function (event, data) {
        if (!this.listeners[event]) {
            return;
        }
        this.listeners[event].forEach(function (cb) { return cb(data); });
    };
    EventDispatcher.prototype.destroy = function () {
        this.listeners = {};
    };
    return EventDispatcher;
}());
/**
 * Creates a dispatch function that can be called inside ProseMirror Plugin
 * to notify listeners about that plugin's state change.
 */
function createDispatch(eventDispatcher) {
    return function (eventName, data) {
        if (!eventName) {
            throw new Error('event name is required!');
        }
        var event = typeof eventName === 'string'
            ? eventName
            : eventName.key;
        eventDispatcher.emit(event, data);
    };
}

var hasRequiredPerformanceAPIs;
function isPerformanceAPIAvailable() {
    if (hasRequiredPerformanceAPIs === undefined) {
        hasRequiredPerformanceAPIs =
            typeof window !== 'undefined' &&
                'performance' in window &&
                [
                    'measure',
                    'clearMeasures',
                    'clearMarks',
                    'getEntriesByName',
                    'getEntriesByType',
                    'now',
                ].every(function (api) { return !!performance[api]; });
    }
    return hasRequiredPerformanceAPIs;
}

var measureMap = new Map();
function startMeasure(measureName) {
    if (!isPerformanceAPIAvailable()) {
        return;
    }
    performance.mark(measureName + "::start");
    measureMap.set(measureName, performance.now());
}
function stopMeasure(measureName, onMeasureComplete) {
    if (!isPerformanceAPIAvailable()) {
        return;
    }
    performance.mark(measureName + "::end");
    var start = onMeasureComplete ? measureMap.get(measureName) : undefined;
    try {
        performance.measure(measureName, measureName + "::start", measureName + "::end");
    }
    catch (error) {
    }
    finally {
        if (onMeasureComplete) {
            var entry = performance.getEntriesByName(measureName).pop();
            if (entry) {
                onMeasureComplete(entry.duration, entry.startTime);
            }
            else if (start) {
                onMeasureComplete(performance.now() - start, start);
            }
        }
        clearMeasure(measureName);
    }
}
function clearMeasure(measureName) {
    if (!isPerformanceAPIAvailable()) {
        return;
    }
    measureMap.delete(measureName);
    performance.clearMarks(measureName + "::start");
    performance.clearMarks(measureName + "::end");
    performance.clearMeasures(measureName);
}

/**
 * Finds all top level nodes affected by the transaction
 * Uses from/to positions in transaction's steps to work out which nodes will
 * be changed by the transaction
 */
var findChangedNodesFromTransaction = function (tr) {
    var nodes = [];
    var steps = (tr.steps || []);
    steps.forEach(function (step) {
        var to = step.to, from = step.from, slice = step.slice;
        var size = slice && slice.content ? slice.content.size : 0;
        var _loop_1 = function (i) {
            if (i <= tr.doc.content.size) {
                var topLevelNode_1 = tr.doc.resolve(i).node(1);
                if (topLevelNode_1 && !nodes.find(function (n) { return n === topLevelNode_1; })) {
                    nodes.push(topLevelNode_1);
                }
            }
        };
        for (var i = from; i <= to + size; i++) {
            _loop_1(i);
        }
    });
    return nodes;
};
var validNode = function (node) {
    try {
        node.check(); // this will throw an error if the node is invalid
    }
    catch (error) {
        return false;
    }
    return true;
};
/** Validates prosemirror nodes, and returns true only if all nodes are valid */
var validateNodes = function (nodes) {
    return nodes.every(validNode);
};
var SelectedState;
(function (SelectedState) {
    SelectedState[SelectedState["selectedInRange"] = 0] = "selectedInRange";
    SelectedState[SelectedState["selectedInside"] = 1] = "selectedInside";
})(SelectedState || (SelectedState = {}));

var getDocStructure = function (doc) {
    try {
        return getBlockNode(doc, 0);
    }
    catch (error) {
        return "Error logging document structure: " + error;
    }
};
var getBlockNode = function (node, pos) {
    var blockNode = {
        type: node.type.name,
        pos: pos,
        nodeSize: node.nodeSize,
    };
    var content = getBlockNodeContent(node.content, pos);
    if (content.length > 0) {
        blockNode.content = content;
    }
    var marks = getMarks(node);
    if (marks.length > 0) {
        blockNode.marks = marks;
    }
    return blockNode;
};
var getBlockNodeContent = function (node, pos) {
    if (!node || !node.content || !node.content.length) {
        return [];
    }
    var blockNodeContent = [];
    var content = node.content;
    if (content[0].isBlock) {
        // children are block nodes
        var prevNode_1;
        blockNodeContent = content.map(function (node) {
            pos += prevNode_1 ? prevNode_1.nodeSize : 1;
            prevNode_1 = node;
            return getBlockNode(node, pos);
        });
    }
    else {
        // children are inline nodes
        var result = getInlineNodes(content, pos);
        blockNodeContent = result.inlineNodes;
        pos = result.pos;
    }
    return blockNodeContent;
};
var getInlineNodes = function (nodes, pos) {
    var inlineNodes = nodes.map(function (node) {
        var nodeSize = node.nodeSize;
        var inlineNode = {
            type: node.type.name,
            pos: pos,
            nodeSize: nodeSize,
        };
        var marks = getMarks(node);
        if (marks.length > 0) {
            inlineNode.marks = marks;
        }
        pos += nodeSize;
        return inlineNode;
    });
    return { inlineNodes: inlineNodes, pos: pos };
};
var getMarks = function (node) {
    return node.marks.map(function (mark) { return mark.type.name; });
};

var ranks = {
    plugins: [
        'underline',
        'blockquote',
        'quickInsert',
        'typeAhead',
    ],
    nodes: [
        'doc',
        'paragraph',
        'text',
        'bulletList',
        'orderedList',
        'listItem',
        'heading',
        'blockquote',
        'codeBlock',
    ],
    marks: [
        // Inline marks
        'link',
        'em',
        'strong',
        'textColor',
        'strike',
        'subsup',
        'underline',
        'code',
        'typeAheadQuery',
        // Block marks
        'alignment',
        'breakout',
        'indentation',
        'annotation',
        //Unsupported mark
        'unsupportedMark',
        'unsupportedNodeAttribute',
    ],
};
function sortByOrder(item) {
    return function (a, b) {
        return ranks[item].indexOf(a.name) - ranks[item].indexOf(b.name);
    };
}

function fixExcludes(marks) {
    var markKeys = Object.keys(marks);
    var markGroups = new Set(markKeys.map(function (mark) { return marks[mark].group; }));
    markKeys.forEach(function (markKey) {
        var mark = marks[markKey];
        if (mark.excludes) {
            mark.excludes = mark.excludes
                .split(' ')
                .filter(function (group) { return markGroups.has(group); })
                .join(' ');
        }
    });
    return marks;
}
function processPluginsList(plugins) {
    /**
     * First pass to collect pluginsOptions
     */
    var pluginsOptions = plugins.reduce(function (acc, plugin) {
        if (plugin.pluginsOptions) {
            Object.keys(plugin.pluginsOptions).forEach(function (pluginName) {
                if (!acc[pluginName]) {
                    acc[pluginName] = [];
                }
                acc[pluginName].push(plugin.pluginsOptions[pluginName]);
            });
        }
        return acc;
    }, {});
    /**
     * Process plugins
     */
    return plugins.reduce(function (acc, plugin) {
        var _a, _b, _c;
        if (plugin.pmPlugins) {
            (_a = acc.pmPlugins).push.apply(_a, plugin.pmPlugins(plugin.name ? pluginsOptions[plugin.name] : undefined));
        }
        if (plugin.nodes) {
            (_b = acc.nodes).push.apply(_b, plugin.nodes());
        }
        if (plugin.marks) {
            (_c = acc.marks).push.apply(_c, plugin.marks());
        }
        if (plugin.contentComponent) {
            acc.contentComponents.push(plugin.contentComponent);
        }
        if (plugin.primaryToolbarComponent) {
            acc.primaryToolbarComponents.push(plugin.primaryToolbarComponent);
        }
        // if (plugin.secondaryToolbarComponent) {
        //   acc.secondaryToolbarComponents.push(plugin.secondaryToolbarComponent);
        // }
        return acc;
    }, {
        nodes: [],
        marks: [],
        pmPlugins: [],
        contentComponents: [],
        primaryToolbarComponents: [],
    });
}
function createPMPlugins(config) {
    var editorConfig = config.editorConfig, rest = __rest(config, ["editorConfig"]);
    return editorConfig.pmPlugins
        .sort(sortByOrder('plugins'))
        .map(function (_a) {
        var plugin = _a.plugin;
        return plugin(rest);
    })
        .filter(function (plugin) { return typeof plugin !== 'undefined'; });
}

var doc = {
    content: 'block+',
};

var blockquote = {
    content: 'paragraph+',
    group: 'block',
    defining: true,
    selectable: false,
    parseDOM: [{ tag: 'blockquote' }],
    toDOM: function () {
        return ['blockquote', 0];
    },
};

/**
 * NOTE: Need this because TS is too smart and inline everything.
 * So we need to give them separate identity.
 * Probably there's a way to solve it but that will need time and exploration.
 * // http://bit.ly/2raXFX5
 * type T1 = X | Y
 * type T2 = A | T1 | B // T2 = A | X | Y | B
 */
// export type ParagraphWithMarksDefinition =
//   | ParagraphWithAlignmentDefinition
//   | ParagraphWithIndentationDefinition;
var paragraph = {
    content: 'inline*',
    group: 'block',
    selectable: false,
    // marks:
    // 'strong code em link strike subsup textColor underline unsupportedMark unsupportedNodeAttribute',
    parseDOM: [{ tag: 'p' }],
    toDOM: function () {
        return ['p', 0];
    },
};

var text = {
    group: 'inline',
    toDebugString: process.env.NODE_ENV !== 'production' ? undefined : function () { return 'text_node'; },
};

function splitBlock(state, dispatch) {
    var _a = state.selection, $from = _a.$from, $to = _a.$to;
    if (state.selection instanceof NodeSelection && state.selection.node.isBlock) {
        if (!$from.parentOffset || !canSplit(state.doc, $from.pos))
            return false;
        if (dispatch)
            dispatch(state.tr.split($from.pos).scrollIntoView());
        return true;
    }
    if (!$from.parent.isBlock)
        return false;
    if (dispatch) {
        var atEnd = $to.parentOffset == $to.parent.content.size;
        var tr = state.tr;
        if (state.selection instanceof TextSelection)
            tr.deleteSelection();
        var deflt = $from.depth == 0 ? undefined : $from.node(-1).contentMatchAt($from.indexAfter(-1)).defaultType;
        var types = atEnd && deflt ? [{ type: deflt }] : undefined;
        var can = canSplit(tr.doc, tr.mapping.map($from.pos), 1, types);
        if (!types && !can && deflt && canSplit(tr.doc, tr.mapping.map($from.pos), 1, deflt && [{ type: deflt }])) {
            types = [{ type: deflt }];
            can = true;
        }
        if (can && deflt) {
            tr.split(tr.mapping.map($from.pos), 1, types);
            var isStuff = !atEnd && !$from.parentOffset && $from.parent.type != deflt;
            var canReplace = $from.node(-1).canReplace($from.index(-1), $from.indexAfter(-1), Fragment.from(deflt.create()));
            if (isStuff && canReplace) {
                tr.setNodeMarkup(tr.mapping.map($from.before()), deflt);
            }
        }
        dispatch(tr.scrollIntoView());
    }
    return true;
}
function createParagraphNear(state, dispatch) {
    var _a = state.selection, $from = _a.$from, $to = _a.$to;
    if ($from.parent.inlineContent || $to.parent.inlineContent)
        return false;
    var type = $from.parent.contentMatchAt($to.indexAfter()).defaultType;
    if (!type || !type.isTextblock)
        return false;
    var empty = type.createAndFill();
    debugger;
    if (dispatch && empty) {
        var side = (!$from.parentOffset && $to.index() < $to.parent.childCount ? $from : $to).pos;
        var tr = state.tr.insert(side, empty);
        tr.setSelection(TextSelection.create(tr.doc, side + 1));
        dispatch(tr.scrollIntoView());
    }
    return true;
}

var basePlugin = function (options) { return ({
    name: 'base',
    pmPlugins: function () {
        var plugins = [
            { name: 'history', plugin: function () { return history(); } },
            { name: 'baseKeyMap', plugin: function () { return keymap(baseKeymap); } },
            {
                name: 'otherKeyMap',
                plugin: function () { return keymap({
                    'Ctrl-Alt-p': createParagraphNear,
                    'Ctrl-Alt-s': splitBlock,
                }); }
            },
        ];
        return plugins;
    },
    nodes: function () {
        return [
            { name: 'doc', node: doc },
            { name: 'paragraph', node: paragraph },
            { name: 'text', node: text },
        ];
    },
}); };

var ReactNodeView = /** @class */ (function () {
    function ReactNodeView(node, view, getPos, portalProviderAPI, eventDispatcher, reactComponentProps, reactComponent, hasContext, viewShouldUpdate) {
        var _this = this;
        if (hasContext === void 0) { hasContext = false; }
        this.handleRef = function (node) { return _this._handleRef(node); };
        this.node = node;
        this.view = view;
        this.getPos = getPos;
        this.portalProviderAPI = portalProviderAPI;
        this.reactComponentProps = reactComponentProps || {};
        this.reactComponent = reactComponent;
        this.hasContext = hasContext;
        this._viewShouldUpdate = viewShouldUpdate;
        this.eventDispatcher = eventDispatcher;
    }
    /**
     * This method exists to move initialization logic out of the constructor,
     * so object can be initialized properly before calling render first time.
     *
     * Example:
     * Instance properties get added to an object only after super call in
     * constructor, which leads to some methods being undefined during the
     * first render.
     */
    ReactNodeView.prototype.init = function () {
        var _this = this;
        this.domRef = this.createDomRef();
        this.setDomAttrs(this.node, this.domRef);
        var _a = this.getContentDOM() || {
            dom: undefined,
            contentDOM: undefined,
        }, contentDOMWrapper = _a.dom, contentDOM = _a.contentDOM;
        if (this.domRef && contentDOMWrapper) {
            this.domRef.appendChild(contentDOMWrapper);
            this.contentDOM = contentDOM ? contentDOM : contentDOMWrapper;
            this.contentDOMWrapper = contentDOMWrapper || contentDOM;
        }
        // @see ED-3790
        // something gets messed up during mutation processing inside of a
        // nodeView if DOM structure has nested plain "div"s, it doesn't see the
        // difference between them and it kills the nodeView
        this.domRef.classList.add(this.node.type.name + "View-content-wrap");
        this.renderReactComponent(function () {
            return _this.render(_this.reactComponentProps, _this.handleRef);
        });
        return this;
    };
    ReactNodeView.prototype.renderReactComponent = function (component) {
        if (!this.domRef || !component) {
            return;
        }
        this.portalProviderAPI.render(component, this.domRef, this.hasContext);
    };
    ReactNodeView.prototype.createDomRef = function () {
        return this.node.isInline
            ? document.createElement('span')
            : document.createElement('div');
    };
    ReactNodeView.prototype.getContentDOM = function () {
        return undefined;
    };
    ReactNodeView.prototype._handleRef = function (node) {
        var contentDOM = this.contentDOMWrapper || this.contentDOM;
        // move the contentDOM node inside the inner reference after rendering
        if (node && contentDOM && !node.contains(contentDOM)) {
            node.appendChild(contentDOM);
        }
    };
    ReactNodeView.prototype.render = function (props, forwardRef) {
        return this.reactComponent ? (React.createElement(this.reactComponent, __assign({ view: this.view, getPos: this.getPos, node: this.node, forwardRef: forwardRef }, props))) : null;
    };
    ReactNodeView.prototype.update = function (node, _decorations, validUpdate) {
        var _this = this;
        if (validUpdate === void 0) { validUpdate = function () { return true; }; }
        // @see https://github.com/ProseMirror/prosemirror/issues/648
        var isValidUpdate = this.node.type === node.type && validUpdate(this.node, node);
        if (!isValidUpdate) {
            return false;
        }
        if (this.domRef && !this.node.sameMarkup(node)) {
            this.setDomAttrs(node, this.domRef);
        }
        // View should not process a re-render if this is false.
        // We dont want to destroy the view, so we return true.
        if (!this.viewShouldUpdate(node)) {
            this.node = node;
            return true;
        }
        this.node = node;
        this.renderReactComponent(function () {
            return _this.render(_this.reactComponentProps, _this.handleRef);
        });
        return true;
    };
    ReactNodeView.prototype.viewShouldUpdate = function (nextNode) {
        if (this._viewShouldUpdate) {
            return this._viewShouldUpdate(nextNode);
        }
        return true;
    };
    /**
     * Copies the attributes from a ProseMirror Node to a DOM node.
     * @param node The Prosemirror Node from which to source the attributes
     */
    ReactNodeView.prototype.setDomAttrs = function (node, element) {
        Object.keys(node.attrs || {}).forEach(function (attr) {
            element.setAttribute(attr, node.attrs[attr]);
        });
    };
    Object.defineProperty(ReactNodeView.prototype, "dom", {
        get: function () {
            return this.domRef;
        },
        enumerable: false,
        configurable: true
    });
    ReactNodeView.prototype.destroy = function () {
        if (!this.domRef) {
            return;
        }
        this.portalProviderAPI.remove(this.domRef);
        this.domRef = undefined;
        this.contentDOM = undefined;
    };
    ReactNodeView.fromComponent = function (component, portalProviderAPI, eventDispatcher, props, viewShouldUpdate) {
        return function (node, view, getPos) {
            return new ReactNodeView(node, view, getPos, portalProviderAPI, eventDispatcher, props, component, false, viewShouldUpdate).init();
        };
    };
    return ReactNodeView;
}());

var BlockQuote = forwardRef(function (props, ref) {
    return (React.createElement(StyledBlockQuote, { ref: ref }));
});
var StyledBlockQuote = styled.blockquote(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  box-sizing: border-box;\n  color: #6a737d;\n  padding: 0 1em;\n  border-left: 4px solid #dfe2e5;\n  margin: 0.2rem 0 0 0;\n  margin-right: 0;\n\n  [dir='rtl'] & {\n    padding-left: 0;\n    padding-right: 4px;\n  }\n\n  &:first-child {\n    margin-top: 0;\n  }\n\n  &::before {\n    content: '';\n  }\n\n  &::after {\n    content: none;\n  }\n\n  & p {\n    display: block;\n  }\n\n  & table,\n  & table:last-child {\n    display: inline-table;\n  }\n"], ["\n  box-sizing: border-box;\n  color: #6a737d;\n  padding: 0 1em;\n  border-left: 4px solid #dfe2e5;\n  margin: 0.2rem 0 0 0;\n  margin-right: 0;\n\n  [dir='rtl'] & {\n    padding-left: 0;\n    padding-right: 4px;\n  }\n\n  &:first-child {\n    margin-top: 0;\n  }\n\n  &::before {\n    content: '';\n  }\n\n  &::after {\n    content: none;\n  }\n\n  & p {\n    display: block;\n  }\n\n  & table,\n  & table:last-child {\n    display: inline-table;\n  }\n"])));
var templateObject_1;

var BlockQuoteView = /** @class */ (function (_super) {
    __extends(BlockQuoteView, _super);
    function BlockQuoteView() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    BlockQuoteView.prototype.getContentDOM = function () {
        var dom = document.createElement('div');
        dom.classList.add(this.node.type.name + "-dom-wrapper");
        return {
            dom: dom
        };
    };
    BlockQuoteView.prototype.render = function (_props, forwardRef) {
        return (React.createElement(BlockQuote, { ref: forwardRef }));
    };
    return BlockQuoteView;
}(ReactNodeView));
function blockQuoteNodeView(portalProviderAPI, eventDispatcher, 
// providerFactory: ProviderFactory,
options) {
    return function (node, view, getPos) {
        return new BlockQuoteView(node, view, getPos, portalProviderAPI, eventDispatcher, {
            // providerFactory,
            options: options,
        }).init();
    };
}

function findBlockQuote(state, selection) {
    var blockquote = state.schema.nodes.blockquote;
    return (findSelectedNodeOfType(blockquote)(selection || state.selection) ||
        findParentNodeOfType(blockquote)(selection || state.selection));
}

var blockquotePluginKey = new PluginKey('blockQuotePlugin');
// export type CodeBlockStateSubscriber = (state: BlockQuoteState) => any;
function blockQuotePluginFactory(dispatch, 
// providerFactory: ProviderFactory,
portalProviderAPI, eventDispatcher, options) {
    return new Plugin({
        state: {
            init: function (_, state) {
                return {
                    blockQuoteActive: false,
                };
            },
            apply: function (tr, pluginState, _oldState, newState) {
                if (tr.docChanged || tr.selectionSet) {
                    var blockQuoteActive = !!findBlockQuote(newState, newState.selection);
                    // const blockQuoteDisabled = !(
                    //   blockQuoteActive ||
                    //   isWrappingPossible(newState.schema.blockquote, newState)
                    // )
                    if (blockQuoteActive !== pluginState.blockQuoteActive) {
                        var nextPluginState = __assign(__assign({}, pluginState), { blockQuoteActive: blockQuoteActive });
                        dispatch(blockquotePluginKey, nextPluginState);
                        return nextPluginState;
                    }
                }
                return pluginState;
            },
        },
        key: blockquotePluginKey,
        props: {
            nodeViews: {
                blockquote: blockQuoteNodeView(portalProviderAPI, eventDispatcher, options),
            },
        },
    });
}

var result = {
    mac: false,
    ie: false,
    ie_version: 0,
    gecko: false,
    chrome: false,
    chrome_version: 0,
    android: false,
    ios: false,
    webkit: false,
};
if (typeof navigator !== 'undefined') {
    var ieEdge = /Edge\/(\d+)/.exec(navigator.userAgent);
    var ieUpTo10 = /MSIE \d/.test(navigator.userAgent);
    var ie11up = /Trident\/(?:[7-9]|\d{2,})\..*rv:(\d+)/.exec(navigator.userAgent);
    result.mac = /Mac/.test(navigator.platform);
    var ie = (result.ie = !!(ieUpTo10 || ie11up || ieEdge));
    result.ie_version = ieUpTo10
        ? document.documentMode || 6
        : ie11up
            ? +ie11up[1]
            : ieEdge
                ? +ieEdge[1]
                : null;
    result.gecko = !ie && /gecko\/\d/i.test(navigator.userAgent);
    result.chrome = !ie && /Chrome\//.test(navigator.userAgent);
    result.chrome_version = parseInt((navigator.userAgent.match(/Chrome\/(\d{2})/) || [])[1], 10);
    result.android = /Android \d/.test(navigator.userAgent);
    result.ios =
        !ie &&
            /AppleWebKit/.test(navigator.userAgent) &&
            /Mobile\/\w+/.test(navigator.userAgent);
    result.webkit =
        !ie &&
            !!document.documentElement &&
            'WebkitAppearance' in document.documentElement.style;
}
var browser = result;
function getUAPrefix() {
    if (browser.chrome) {
        return 'ua-chrome';
    }
    else if (browser.ie) {
        return 'ua-ie';
    }
    else if (browser.gecko) {
        return 'ua-firefox';
    }
    return '';
}

var toggleBold = makeKeyMapWithCommon('Bold', 'Mod-b');
var toggleItalic = makeKeyMapWithCommon('Italic', 'Mod-i');
var toggleUnderline = makeKeyMapWithCommon('Underline', 'Mod-u');
var toggleCode = makeKeyMapWithCommon('Code', 'Mod-Shift-m');
var setNormalText = makeKeyMapWithCommon('Normal text', 'Mod-Alt-0');
var toggleHeading1 = makeKeyMapWithCommon('Heading 1', 'Mod-Alt-1');
var toggleBlockQuote = makeKeyMapWithCommon('Quote', 'Ctrl-Alt-b');
var insertNewLine = makeKeyMapWithCommon('Insert new line', 'Shift-Enter');
var shiftBackspace = makeKeyMapWithCommon('Shift Backspace', 'Shift-Backspace');
var splitCodeBlock = makeKeyMapWithCommon('Split code block', 'Enter');
var undo = makeKeyMapWithCommon('Undo', 'Mod-z');
var moveUp = makeKeyMapWithCommon('Move up', 'ArrowUp');
var moveDown = makeKeyMapWithCommon('Move down', 'ArrowDown');
var moveLeft = makeKeyMapWithCommon('Move left', 'ArrowLeft');
var moveRight = makeKeyMapWithCommon('Move right', 'ArrowRight');
var openHelp = makeKeyMapWithCommon('Open Help', 'Mod-/');
var addLink = makeKeyMapWithCommon('Link', 'Mod-k');
var addInlineComment = makeKeyMapWithCommon('Annotate', 'Mod-Alt-c');
var submit = makeKeyMapWithCommon('Submit Content', 'Mod-Enter');
var enter = makeKeyMapWithCommon('Enter', 'Enter');
var shiftEnter = makeKeyMapWithCommon('Shift Enter', 'Shift-Enter');
var tab = makeKeyMapWithCommon('Tab', 'Tab');
var backspace = makeKeyMapWithCommon('Backspace', 'Backspace');
var deleteKey = makeKeyMapWithCommon('Delete', 'Delete');
var space = makeKeyMapWithCommon('Space', 'Space');
var escape = makeKeyMapWithCommon('Escape', 'Escape');
var nextCell = makeKeyMapWithCommon('Next cell', 'Tab');
var previousCell = makeKeyMapWithCommon('Previous cell', 'Shift-Tab');
var toggleTable = makeKeyMapWithCommon('Table', 'Shift-Alt-t');
var cut = makeKeyMapWithCommon('Cut', 'Mod-x');
var copy = makeKeyMapWithCommon('Copy', 'Mod-c');
var paste = makeKeyMapWithCommon('Paste', 'Mod-v');
var altPaste = makeKeyMapWithCommon('Paste', 'Mod-Shift-v');
var find = makeKeyMapWithCommon('Find', 'Mod-f');
var arrowKeysMap = {
    // for reference: https://wincent.com/wiki/Unicode_representations_of_modifier_keys
    ARROWLEFT: '\u2190',
    ARROWRIGHT: '\u2192',
    ARROWUP: '\u2191',
    ARROWDOWN: '\u2193',
};
var TooltipShortcut = styled.span(templateObject_1$1 || (templateObject_1$1 = __makeTemplateObject(["\n  border-radius: 2px;\n  background-color: blue;\n  padding: 0 2px;\n"], ["\n  border-radius: 2px;\n  background-color: blue;\n  padding: 0 2px;\n"])));
function formatShortcut(keymap) {
    var shortcut;
    if (browser.mac) {
        // for reference: https://wincent.com/wiki/Unicode_representations_of_modifier_keys
        shortcut = keymap.mac
            .replace(/Cmd/i, '\u2318')
            .replace(/Shift/i, '\u21E7')
            .replace(/Ctrl/i, '\u2303')
            .replace(/Alt/i, '\u2325')
            .replace(/Backspace/i, '\u232B')
            .replace(/Enter/i, '\u23CE');
    }
    else {
        shortcut = keymap.windows.replace(/Backspace/i, '\u232B');
    }
    var keys = shortcut.split('-');
    var lastKey = keys[keys.length - 1];
    if (lastKey.length === 1) {
        // capitalise single letters
        lastKey = lastKey.toUpperCase();
    }
    keys[keys.length - 1] = arrowKeysMap[lastKey.toUpperCase()] || lastKey;
    return keys.join(browser.mac ? '' : '+');
}
function tooltip(keymap, description) {
    if (keymap) {
        var shortcut = formatShortcut(keymap);
        return description ? description + " " + shortcut : shortcut;
    }
    return;
}
var ToolTipContent = React.memo(function (_a) {
    var description = _a.description, shortcutOverride = _a.shortcutOverride, keymap = _a.keymap;
    var shortcut = shortcutOverride || (keymap && formatShortcut(keymap));
    return shortcut || description ? (React.createElement(React.Fragment, null,
        description,
        shortcut && description && '\u00A0',
        shortcut && React.createElement(TooltipShortcut, null, shortcut))) : null;
});
function makeKeymap(description, windows, mac, common) {
    return {
        description: description,
        windows: windows,
        mac: mac,
        common: common,
    };
}
function makeKeyMapWithCommon(description, common) {
    var windows = common.replace(/Mod/i, 'Ctrl');
    var mac = common.replace(/Mod/i, 'Cmd');
    return makeKeymap(description, windows, mac, common);
}
function bindKeymapWithCommand(shortcut, cmd, keymap) {
    var oldCmd = keymap[shortcut];
    var newCmd = cmd;
    if (keymap[shortcut]) {
        newCmd = function (state, dispatch, editorView) {
            return oldCmd(state, dispatch) || cmd(state, dispatch, editorView);
        };
    }
    keymap[shortcut] = newCmd;
}
var templateObject_1$1;

var createNewBlockQuote = function () { return function (state, dispatch) {
    var _a = state.selection, $from = _a.$from, $to = _a.$to;
    var blockquote = state.schema.nodes.blockquote;
    var empty = blockquote.createAndFill();
    var endOfBlock = $from.end();
    if (empty && dispatch) {
        var tr = state.tr.insert(endOfBlock + 1, empty);
        dispatch(tr);
    }
    return false;
}; };
// export const toggleBlockQuote = (
//   state: EditorState,
//   dispatch: (tr: Transaction) => void,
//   view: EditorView,
// ): boolean => {
//   const { selection } = state;
//   const fromNode = selection.$from.node(selection.$from.depth - 2);
//   const endNode = selection.$to.node(selection.$to.depth - 2);
//   if (
//     !fromNode ||
//     fromNode.type.name !== listType ||
//     !endNode ||
//     endNode.type.name !== listType
//   ) {
//     return toggleListCommandWithAnalytics(inputMethod, listType)(
//       state,
//       dispatch,
//       view,
//     );
//   } else {
//     const depth = rootListDepth(selection.$to, state.schema.nodes);
//     let tr = liftFollowingList(
//       state,
//       selection.$to.pos,
//       selection.$to.end(depth),
//       depth || 0,
//       state.tr,
//     );
//     tr = liftSelectionList(state, tr);
//     dispatch(tr);
//     return true;
//   }
// };
// export function toggleListCommand(
//   listType: 'bulletList' | 'orderedList',
// ): Command {
//   return function (state, dispatch, view) {
//     if (dispatch) {
//       dispatch(
//         state.tr.setSelection(
//           adjustSelectionInList(state.doc, state.selection as TextSelection),
//         ),
//       );
//     }
//     if (!view) {
//       return false;
//     }
//     state = view.state;
//     const {
//       doc,
//       selection: { $from, $to },
//     } = state;
//     const listNodeType = state.schema.nodes[listType];
//     // find closest parent of listNodeType from start of selection
//     const listParentPos = findParentNodeOfTypeClosestToPos(
//       doc.resolve($from.pos),
//       listNodeType,
//     );
//     // determine if end of selection is outside of that list (if selection is in a list at all)
//     const isSameListTypeSelected =
//       listParentPos &&
//       $to.pos <= listParentPos.pos + listParentPos.node.nodeSize;
//     if (isInsideList(state, listType) && isSameListTypeSelected) {
//       // Untoggles list
//       return liftListItems()(state, dispatch);
//     } else {
//       // Converts list type e.g. bullet_list -> ordered_list if needed
//       if (!isSameListTypeSelected) {
//         liftListItems()(state, dispatch);
//         state = view.state;
//       }
//       // Remove any invalid marks that are not supported
//       const tr = sanitiseSelectionMarksForWrapping(state, listNodeType);
//       if (tr && dispatch) {
//         dispatch(tr);
//         state = view.state;
//       }
//       // Wraps selection in list
//       return wrapInList(listNodeType)(state, dispatch);
//     }
//   };
// }
// export function toggleBulletList(
//   view: EditorView,
//   inputMethod: InputMethod = INPUT_METHOD.TOOLBAR,
// ) {
//   return toggleList(view.state, view.dispatch, view, 'bulletList', inputMethod);
// }
// export function toggleOrderedList(
//   view: EditorView,
//   inputMethod: InputMethod = INPUT_METHOD.TOOLBAR,
// ) {
//   return toggleList(
//     view.state,
//     view.dispatch,
//     view,
//     'orderedList',
//     inputMethod,
//   );
// }

function keymapPlugin() {
    var keymapObj = {};
    bindKeymapWithCommand(toggleBlockQuote.common, createNewBlockQuote(), keymapObj);
    return keymap(keymapObj);
}

function IconCode(_a) {
    var _b = _a.label, label = _b === void 0 ? '' : _b;
    return (React.createElement("svg", { "aria-label": label, width: 40, height: 40, fillRule: "evenodd" },
        React.createElement("path", { fill: "#fff", d: "M0 0h40v40H0z" }),
        React.createElement("path", { fill: "#ebecf0", d: "M13 6h26v28H13z" }),
        React.createElement("path", { d: "M9 6h4v28H9a2 2 0 01-2-2V8a2 2 0 012-2z", fill: "#dfe1e6" }),
        React.createElement("g", { fill: "#a5adba" },
            React.createElement("rect", { x: 9, y: 9, width: 2, height: 1, rx: 0.5 }),
            React.createElement("rect", { x: 16, y: 9, width: 9, height: 1, rx: 0.5 }),
            React.createElement("rect", { x: 22, y: 21, width: 9, height: 1, rx: 0.5 }),
            React.createElement("rect", { x: 22, y: 29, width: 9, height: 1, rx: 0.5 })),
        React.createElement("g", { fill: "#4c9aff" },
            React.createElement("rect", { x: 28, y: 25, width: 9, height: 1, rx: 0.5 }),
            React.createElement("rect", { x: 16, y: 13, width: 13, height: 1, rx: 0.5 })),
        React.createElement("g", { fill: "#ff7452" },
            React.createElement("rect", { x: 16, y: 17, width: 13, height: 1, rx: 0.5 }),
            React.createElement("rect", { x: 19, y: 25, width: 7, height: 1, rx: 0.5 })),
        React.createElement("g", { fill: "#a5adba" },
            React.createElement("rect", { x: 9, y: 13, width: 2, height: 1, rx: 0.5 }),
            React.createElement("rect", { x: 9, y: 17, width: 2, height: 1, rx: 0.5 }),
            React.createElement("rect", { x: 9, y: 21, width: 2, height: 1, rx: 0.5 }),
            React.createElement("rect", { x: 9, y: 25, width: 2, height: 1, rx: 0.5 }),
            React.createElement("rect", { x: 9, y: 29, width: 2, height: 1, rx: 0.5 }))));
}

var blockQuotePlugin = function (options) {
    if (options === void 0) { options = {}; }
    return ({
        name: 'blockquote',
        nodes: function () {
            return [{ name: 'blockquote', node: blockquote }];
        },
        pmPlugins: function () {
            var plugins = [
                {
                    name: 'blockquote',
                    plugin: function (_a) {
                        var dispatch = _a.dispatch, 
                        // providerFactory,
                        portalProviderAPI = _a.portalProviderAPI, eventDispatcher = _a.eventDispatcher;
                        return blockQuotePluginFactory(dispatch, 
                        // providerFactory,
                        portalProviderAPI, eventDispatcher, options);
                    },
                },
                { name: 'blockquoteKeyMap', plugin: function () { return keymapPlugin(); } },
            ];
            return plugins;
        },
        // primaryToolbarComponent({
        //   editorView,
        //   popupsMountPoint,
        //   popupsBoundariesElement,
        //   popupsScrollableElement,
        //   toolbarSize,
        //   disabled,
        //   isToolbarReducedSpacing,
        //   eventDispatcher,
        // }) {
        //   return (
        //     <WithPluginState
        //       editorView={editorView}
        //       eventDispatcher={eventDispatcher}
        //       plugins={{
        //         pluginState: blockquotePluginKey,
        //       }}
        //       render={({ pluginState }) => {
        //         return (
        //           <ToolbarBlockType
        //             isDisabled={disabled}
        //             isReducedSpacing={isToolbarReducedSpacing}
        //             setBlockType={boundSetBlockType}
        //             pluginState={pluginState}
        //             popupsMountPoint={popupsMountPoint}
        //             popupsBoundariesElement={popupsBoundariesElement}
        //             popupsScrollableElement={popupsScrollableElement}
        //           />
        //         );
        //       }}
        //     />
        //   );
        // },
        pluginsOptions: {
            quickInsert: [
                {
                    id: 'blockquote',
                    title: 'Quote',
                    description: 'Quote some text',
                    priority: 1300,
                    keyshortcut: tooltip(toggleBlockQuote),
                    icon: function () { return React.createElement(IconCode, { label: 'Quote' }); },
                    action: function (insert, state) {
                        var tr = insert(state.schema.nodes.blockquote.createChecked({}, state.schema.nodes.paragraph.createChecked()));
                        return tr;
                    },
                },
            ],
        },
    });
};

var pluginKey = new PluginKey('quickInsertPluginKey');

var options = {
    threshold: 0.3,
    keys: [
        // Weights must sum to <= 1.0
        { name: 'title', weight: 0.5 },
        { name: 'priority', weight: 0.3 },
        { name: 'keywords', weight: 0.15 },
        { name: 'description', weight: 0.04 },
        { name: 'keyshortcut', weight: 0.01 },
    ],
};
function dedupe(list, iteratee) {
    /**
                .,
      .      _,'f----.._
      |\ ,-'"/  |     ,'
      |,_  ,--.      /
      /,-. ,'`.     (_
      f  o|  o|__     "`-.
      ,-._.,--'_ `.   _.,-`
      `"' ___.,'` j,-'
        `-.__.,--'
      Gotta go fast!
   */
    if (list === void 0) { list = []; }
    if (iteratee === void 0) { iteratee = function (p) { return p; }; }
    var seen = new Set();
    list.forEach(function (l) { return seen.add(iteratee(l)); });
    return list.filter(function (l) {
        var it = iteratee(l);
        if (seen.has(it)) {
            seen.delete(it);
            return true;
        }
        return false;
    });
}
function find$1(query, items) {
    var fuse = new Fuse(items, options);
    if (query === '') {
        // Copy and sort list by priority
        return items
            .slice(0)
            .sort(function (a, b) {
            return (a.priority || Number.POSITIVE_INFINITY) -
                (b.priority || Number.POSITIVE_INFINITY);
        });
    }
    return fuse.search(query).map(function (s) { return s.item; });
}
var searchQuickInsertItems = function (quickInsertState, options) { return function (query, category) {
    var defaultItems = options && options.disableDefaultItems
        ? []
        : quickInsertState.lazyDefaultItems();
    var providedItems = quickInsertState.providedItems;
    var items = providedItems
        ? dedupe(__spreadArrays(defaultItems, providedItems), function (item) { return item.title; })
        : defaultItems;
    return find$1(query || '', items);
}; };

var quickInsertPlugin = function (options) { return ({
    name: 'quickInsert',
    pmPlugins: function (quickInsert) {
        return [
            {
                name: 'quickInsert',
                plugin: function (_a) {
                    var providerFactory = _a.providerFactory, dispatch = _a.dispatch;
                    return quickInsertPluginFactory(quickInsert, providerFactory, dispatch);
                },
            },
        ];
    },
    pluginsOptions: {
        typeAhead: {
            trigger: '/',
            headless: options ? options.headless : undefined,
            getItems: function (query, state, _tr, dispatch) {
                var quickInsertState = pluginKey.getState(state);
                return searchQuickInsertItems(quickInsertState, options)(query);
            },
            selectItem: function (state, item, insert) {
                return item.action(insert, state);
            },
        },
    },
}); };
var itemsCache = {};
var processItems = function (items) {
    if (!itemsCache['en']) {
        itemsCache['en'] = items.reduce(function (acc, item) {
            // if (typeof item === 'function') {
            //   return acc.concat(item('en'));
            // }
            return acc.concat(item);
        }, []);
    }
    return itemsCache['en'];
};
var setProviderState = function (providerState) { return function (state, dispatch) {
    if (dispatch) {
        dispatch(state.tr.setMeta(pluginKey, providerState));
    }
    return true;
}; };
function quickInsertPluginFactory(quickInsertItems, providerFactory, dispatch) {
    return new Plugin({
        key: pluginKey,
        state: {
            init: function () {
                return {
                    isElementBrowserModalOpen: false,
                    // lazy so it doesn't run on editor initialization
                    lazyDefaultItems: function () { return processItems(quickInsertItems || []); },
                };
            },
            apply: function (tr, pluginState) {
                var meta = tr.getMeta(pluginKey);
                if (meta) {
                    var changed = Object.keys(meta).some(function (key) {
                        return pluginState[key] !== meta[key];
                    });
                    if (changed) {
                        var newState = __assign(__assign({}, pluginState), meta);
                        dispatch(pluginKey, newState);
                        return newState;
                    }
                }
                return pluginState;
            },
        },
        view: function (editorView) {
            var _this = this;
            var providerHandler = function (_name, providerPromise) { return __awaiter(_this, void 0, void 0, function () {
                var provider, providedItems, e_1;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            if (!providerPromise) return [3 /*break*/, 5];
                            _a.label = 1;
                        case 1:
                            _a.trys.push([1, 4, , 5]);
                            return [4 /*yield*/, providerPromise];
                        case 2:
                            provider = _a.sent();
                            return [4 /*yield*/, provider.getItems()];
                        case 3:
                            providedItems = _a.sent();
                            setProviderState({ provider: provider, providedItems: providedItems })(editorView.state, editorView.dispatch);
                            return [3 /*break*/, 5];
                        case 4:
                            e_1 = _a.sent();
                            // eslint-disable-next-line no-console
                            console.error('Error getting items from quick insert provider', e_1);
                            return [3 /*break*/, 5];
                        case 5: return [2 /*return*/];
                    }
                });
            }); };
            providerFactory.subscribe('quickInsertProvider', providerHandler);
            return {
                destroy: function () {
                    providerFactory.unsubscribe('quickInsertProvider', providerHandler);
                },
            };
        },
    });
}

// # What do marks exist?
// Marks group for search queries.
var SEARCH_QUERY = 'searchQuery';

// import themed from './utils/themed';
// Blues
var B50 = '#DEEBFF';
var B100 = '#4C9AFF';
var B400 = '#0052CC';
// Neutrals
var N0 = '#FFFFFF';
var N20 = '#F4F5F7';
var N30 = '#EBECF0';
var N100 = '#7A869A';
var N200 = '#6B778C';
var N500 = '#42526E';
var N800 = '#172B4D';
// ATTENTION: update the tints if you update this
var N900 = '#091E42';
var N50A = 'rgba(9, 30, 66, 0.25)';
var N60A = 'rgba(9, 30, 66, 0.31)';
// Themed colors
var background = N0; //themed({ light: N0, dark: DN30 });
var backgroundActive = B50; //themed({ light: B50, dark: B75 });
var backgroundHover = N30; //themed({ light: N30, dark: DN70 });
var text$1 = N900; // themed({ light: N900, dark: DN600 });
var textHover = N800; //themed({ light: N800, dark: DN600 });
var textActive = B400; //themed({ light: B400, dark: B400 });
// export const subtleText = themed({ light: N200, dark: DN300 });
// export const placeholderText = themed({ light: N100, dark: DN200 });
// export const heading = themed({ light: N800, dark: DN600 });
// export const subtleHeading = themed({ light: N200, dark: DN300 });
// export const codeBlock = themed({ light: N20, dark: DN50 });
// export const link = themed({ light: B400, dark: B100 });
// export const linkHover = themed({ light: B300, dark: B200 });
// export const linkActive = themed({ light: B500, dark: B100 });
// export const linkOutline = themed({ light: B100, dark: B200 });
// export const primary = themed({ light: B400, dark: B100 });
// export const blue = themed({ light: B400, dark: B100 });
// export const teal = themed({ light: T300, dark: T200 });
// export const purple = themed({ light: P300, dark: P100 });
// export const red = themed({ light: R300, dark: R300 });
// export const yellow = themed({ light: Y300, dark: Y300 });
// export const green = themed({ light: G300, dark: G300 });
// export const skeleton = () => N20A;

var typeAheadQuery = {
    inclusive: true,
    group: SEARCH_QUERY,
    parseDOM: [{ tag: 'span[data-type-ahead-query]' }],
    toDOM: function (node) {
        return [
            'span',
            {
                'data-type-ahead-query': 'true',
                'data-trigger': node.attrs.trigger,
                style: "color: " + B400,
            },
        ];
    },
    attrs: {
        trigger: { default: '' },
    },
};

/**
 * Wraps component in a high order component that watches state changes of given plugins
 * and passes those states to the wrapped component.
 *
 * Example:
 * <WithPluginState
 *   eventDispatcher={eventDispatcher}
 *   editorView={editorView}
 *   plugins={{
 *     hyperlink: hyperlinkPluginKey
 *   }}
 *   render={renderComponent}
 * />
 *
 * renderComponent: ({ hyperlink }) => React.Component;
 */
var WithPluginState = /** @class */ (function (_super) {
    __extends(WithPluginState, _super);
    function WithPluginState(props, context) {
        var _this = _super.call(this, props) || this;
        _this.listeners = {};
        _this.debounce = null;
        _this.notAppliedState = {};
        _this.isSubscribed = false;
        _this.state = {};
        _this.handlePluginStateChange = function (propName, pluginName, skipEqualityCheck) { return function (pluginState) {
            var _a;
            // skipEqualityCheck is being used for old plugins since they are mutating plugin state instead of creating a new one
            if (_this.state[propName] !== pluginState || skipEqualityCheck) {
                _this.updateState({
                    stateSubset: (_a = {}, _a[propName] = pluginState, _a),
                    pluginName: pluginName,
                });
            }
        }; };
        /**
         * Debounces setState calls in order to reduce number of re-renders caused by several plugin state changes.
         */
        _this.updateState = function (_a) {
            var stateSubset = _a.stateSubset, pluginName = _a.pluginName;
            _this.notAppliedState = __assign(__assign({}, _this.notAppliedState), stateSubset);
            if (_this.debounce) {
                window.clearTimeout(_this.debounce);
            }
            _this.debounce = window.setTimeout(function () {
                _this.setState(_this.notAppliedState);
                _this.debounce = null;
                _this.notAppliedState = {};
            }, 0);
        };
        _this.onContextUpdate = function () {
            _this.subscribe(_this.props);
        };
        _this.state = _this.getPluginsStates(props.plugins, _this.getEditorView(props, context));
        return _this;
    }
    WithPluginState.prototype.getEditorView = function (maybeProps, maybeContext) {
        var props = maybeProps || this.props;
        var context = maybeContext || this.context;
        return (props.editorView ||
            (context &&
                context.editorActions &&
                context.editorActions._privateGetEditorView()));
    };
    WithPluginState.prototype.getEventDispatcher = function (maybeProps) {
        var props = maybeProps || this.props;
        return (props.eventDispatcher ||
            (this.context &&
                this.context.editorActions &&
                this.context.editorActions._privateGetEventDispatcher()));
    };
    WithPluginState.prototype.getPluginsStates = function (plugins, editorView) {
        if (!editorView || !plugins) {
            return {};
        }
        return Object.keys(plugins).reduce(function (acc, propName) {
            var pluginKey = plugins[propName];
            if (!pluginKey) {
                return acc;
            }
            acc[propName] = pluginKey.getState(editorView.state);
            return acc;
        }, {});
    };
    WithPluginState.prototype.subscribe = function (props) {
        var _this = this;
        var plugins = props.plugins;
        var eventDispatcher = this.getEventDispatcher(props);
        var editorView = this.getEditorView(props);
        if (!eventDispatcher || !editorView || this.isSubscribed) {
            return;
        }
        this.isSubscribed = true;
        var pluginsStates = this.getPluginsStates(plugins, editorView);
        this.setState(pluginsStates);
        Object.keys(plugins).forEach(function (propName) {
            var pluginKey = plugins[propName];
            if (!pluginKey) {
                return;
            }
            var pluginName = pluginKey.key;
            var pluginState = pluginsStates[propName];
            var isPluginWithSubscribe = pluginState && pluginState.subscribe;
            var handler = _this.handlePluginStateChange(propName, pluginName, isPluginWithSubscribe);
            if (isPluginWithSubscribe) {
                pluginState.subscribe(handler);
            }
            else {
                eventDispatcher.on(pluginKey.key, handler);
            }
            _this.listeners[pluginKey.key] = { handler: handler, pluginKey: pluginKey };
        });
    };
    WithPluginState.prototype.unsubscribe = function () {
        var _this = this;
        var eventDispatcher = this.getEventDispatcher();
        var editorView = this.getEditorView();
        if (!eventDispatcher || !editorView || !this.isSubscribed) {
            return;
        }
        Object.keys(this.listeners).forEach(function (key) {
            var pluginState = _this.listeners[key].pluginKey.getState(editorView.state);
            if (pluginState && pluginState.unsubscribe) {
                pluginState.unsubscribe(_this.listeners[key].handler);
            }
            else {
                eventDispatcher.off(key, _this.listeners[key].handler);
            }
        });
        this.listeners = [];
    };
    WithPluginState.prototype.subscribeToContextUpdates = function (context) {
        if (context && context.editorActions) {
            context.editorActions._privateSubscribe(this.onContextUpdate);
        }
    };
    WithPluginState.prototype.unsubscribeFromContextUpdates = function (context) {
        if (context && context.editorActions) {
            context.editorActions._privateUnsubscribe(this.onContextUpdate);
        }
    };
    WithPluginState.prototype.componentDidMount = function () {
        this.subscribe(this.props);
        this.subscribeToContextUpdates(this.context);
    };
    WithPluginState.prototype.UNSAFE_componentWillReceiveProps = function (nextProps) {
        if (!this.isSubscribed) {
            this.subscribe(nextProps);
        }
    };
    WithPluginState.prototype.componentWillUnmount = function () {
        if (this.debounce) {
            window.clearTimeout(this.debounce);
        }
        this.unsubscribeFromContextUpdates(this.context);
        this.unsubscribe();
    };
    WithPluginState.prototype.render = function () {
        var render = this.props.render;
        return render(this.state);
    };
    WithPluginState.displayName = 'WithPluginState';
    WithPluginState.contextTypes = {
        editorActions: PropTypes.object,
        editorConfig: PropTypes.object,
    };
    return WithPluginState;
}(React.Component));

function defaultInputRuleHandler(inputRule, isBlockNodeRule) {
    if (isBlockNodeRule === void 0) { isBlockNodeRule = false; }
    var originalHandler = inputRule.handler;
    inputRule.handler = function (state, match, start, end) {
        // Skip any input rule inside code
        // https://product-fabric.atlassian.net/wiki/spaces/E/pages/37945345/Editor+content+feature+rules#Editorcontent/featurerules-Rawtextblocks
        var unsupportedMarks = isBlockNodeRule
            ? hasUnsupportedMarkForBlockInputRule(state, start, end)
            : hasUnsupportedMarkForInputRule(state, start, end);
        var $from = state.selection.$from;
        if ($from.parent.type.spec.code ||
            !(state.selection instanceof TextSelection) ||
            unsupportedMarks) {
            return;
        }
        return originalHandler(state, match, start, end);
    };
    return inputRule;
}
// The chrome profiler groups all input rules together, making it tricky to detect which one is slow.
// This instrumentated method will add new marks with the format input-rule:<pluginName> making it
// pretty straightforward to find issues.
function instrumentedInputRule(pluginName, _a) {
    var rules = _a.rules;
    var plugin = inputRules({ rules: rules });
    if (process.env.NODE_ENV !== 'production') {
        var handleTextInput_1 = plugin.props.handleTextInput;
        var timerId_1 = "input-rule:" + pluginName;
        plugin.props.handleTextInput = function (view, from, to, text) {
            startMeasure(timerId_1);
            // @ts-ignore
            var result = handleTextInput_1(view, from, to, text);
            stopMeasure(timerId_1, function () { });
            return result;
        };
    }
    return plugin;
}
function createInputRule(match, handler, isBlockNodeRule) {
    if (isBlockNodeRule === void 0) { isBlockNodeRule = false; }
    return defaultInputRuleHandler(new InputRule(match, handler), isBlockNodeRule);
}
// ProseMirror uses the Unicode Character 'OBJECT REPLACEMENT CHARACTER' (U+FFFC) as text representation for
// leaf nodes, i.e. nodes that don't have any content or text property (e.g. hardBreak, emoji, mention, rule)
// It was introduced because of https://github.com/ProseMirror/prosemirror/issues/262
// This can be used in an input rule regex to be able to include or exclude such nodes.
var leafNodeReplacementCharacter = '\ufffc';
var hasUnsupportedMarkForBlockInputRule = function (state, start, end) {
    var doc = state.doc, marks = state.schema.marks;
    var unsupportedMarksPresent = false;
    var isUnsupportedMark = function (node) {
        return node.type === marks.code ||
            node.type === marks.link ||
            node.type === marks.typeAheadQuery;
    };
    doc.nodesBetween(start, end, function (node) {
        unsupportedMarksPresent =
            unsupportedMarksPresent ||
                node.marks.filter(isUnsupportedMark).length > 0;
    });
    return unsupportedMarksPresent;
};
var hasUnsupportedMarkForInputRule = function (state, start, end) {
    var doc = state.doc, marks = state.schema.marks;
    var unsupportedMarksPresent = false;
    var isCodemark = function (mark) { return mark.type === marks.code; };
    doc.nodesBetween(start, end, function (node) {
        unsupportedMarksPresent =
            unsupportedMarksPresent || node.marks.filter(isCodemark).length > 0;
    });
    return unsupportedMarksPresent;
};

// import { FakeTextCursorSelection } from '../plugins/fake-text-cursor/cursor';
function isMarkTypeCompatibleWithMark(markType, mark) {
    return !mark.type.excludes(markType) && !markType.excludes(mark.type);
}
function isMarkTypeAllowedInNode(markType, state) {
    return toggleMark(markType)(state);
}
/**
 * Check if a mark is allowed at the current selection / cursor based on a given state.
 * This method looks at both the currently active marks on the transaction, as well as
 * the node and marks at the current selection to determine if the given mark type is
 * allowed.
 */
function isMarkTypeAllowedInCurrentSelection(markType, state) {
    // if (state.selection instanceof FakeTextCursorSelection) {
    //   return true;
    // }
    if (!isMarkTypeAllowedInNode(markType, state)) {
        return false;
    }
    var _a = state.selection, empty = _a.empty, $cursor = _a.$cursor, ranges = _a.ranges;
    if (empty && !$cursor) {
        return false;
    }
    var isCompatibleMarkType = function (mark) {
        return isMarkTypeCompatibleWithMark(markType, mark);
    };
    // Handle any new marks in the current transaction
    if (state.tr.storedMarks &&
        !state.tr.storedMarks.every(isCompatibleMarkType)) {
        return false;
    }
    if ($cursor) {
        return $cursor.marks().every(isCompatibleMarkType);
    }
    // Check every node in a selection - ensuring that it is compatible with the current mark type
    return ranges.every(function (_a) {
        var $from = _a.$from, $to = _a.$to;
        var allowedInActiveMarks = $from.depth === 0 ? state.doc.marks.every(isCompatibleMarkType) : true;
        state.doc.nodesBetween($from.pos, $to.pos, function (node) {
            allowedInActiveMarks =
                allowedInActiveMarks && node.marks.every(isCompatibleMarkType);
        });
        return allowedInActiveMarks;
    });
}

var pluginKey$1 = new PluginKey('typeAheadPlugin');

function findQueryMark(mark, doc, from, to) {
    var queryMark = { start: -1, end: -1 };
    doc.nodesBetween(from, to, function (node, pos) {
        if (queryMark.start === -1 && mark.isInSet(node.marks)) {
            queryMark = {
                start: pos,
                end: pos + Math.max(node.textContent.length, 1),
            };
        }
    });
    return queryMark;
}
function findTypeAheadQuery(state) {
    var doc = state.doc, schema = state.schema;
    var typeAheadQuery = schema.marks.typeAheadQuery;
    var _a = state.selection, from = _a.from, to = _a.to;
    return findQueryMark(typeAheadQuery, doc, from - 1, to);
}

var dismissCommand = function () { return function (state, dispatch) {
    var queryMark = findTypeAheadQuery(state);
    if (queryMark === null) {
        return false;
    }
    var start = queryMark.start, end = queryMark.end;
    var schema = state.schema;
    var markType = schema.marks.typeAheadQuery;
    if (start === -1) {
        return false;
    }
    var typeAheadHandler = pluginKey$1.getState(state).typeAheadHandler;
    if (typeAheadHandler && typeAheadHandler.dismiss) {
        typeAheadHandler.dismiss(state);
    }
    if (dispatch) {
        dispatch(state.tr.removeMark(start, end, markType).removeStoredMark(markType));
    }
    return true;
}; };

function insertTypeAheadQuery(trigger, replaceLastChar) {
    if (replaceLastChar === void 0) { replaceLastChar = false; }
    return function (state, dispatch) {
        if (!dispatch) {
            return false;
        }
        if (replaceLastChar) {
            var tr = state.tr, selection = state.selection;
            var marks = selection.$from.marks();
            dispatch(tr
                .setSelection(TextSelection.create(tr.doc, selection.$from.pos - 1, selection.$from.pos))
                .replaceSelectionWith(state.doc.type.schema.text(trigger, __spreadArrays([
                state.schema.marks.typeAheadQuery.create({ trigger: trigger })
            ], marks)), false));
            return true;
        }
        dispatch(safeInsert(state.schema.text(trigger, [
            state.schema.marks.typeAheadQuery.create({ trigger: trigger }),
        ]))(state.tr));
        return true;
    };
}

var ACTIONS = {
    SELECT_PREV: 'SELECT_PREV',
    SELECT_NEXT: 'SELECT_NEXT',
    SELECT_CURRENT: 'SELECT_CURRENT',
    SET_CURRENT_INDEX: 'SET_CURRENT_INDEX',
    SET_QUERY: 'SET_QUERY',
    ITEMS_LIST_UPDATED: 'ITEMS_LIST_UPDATED',
};

var itemsListUpdated = function (items) { return function (state, dispatch) {
    if (dispatch) {
        dispatch(state.tr.setMeta(pluginKey$1, {
            action: ACTIONS.ITEMS_LIST_UPDATED,
            items: items,
        }));
    }
    return true;
}; };

var normaliseNestedLayout = function (state, node) {
    if (state.selection.$from.depth > 1) {
        if (node.attrs.layout && node.attrs.layout !== 'default') {
            return node.type.createChecked(__assign(__assign({}, node.attrs), { layout: 'default' }), node.content, node.marks);
        }
        // If its a breakout layout, we can remove the mark
        // Since default isn't a valid breakout mode.
        var breakoutMark = state.schema.marks.breakout;
        if (breakoutMark && breakoutMark.isInSet(node.marks)) {
            var newMarks = breakoutMark.removeFromSet(node.marks);
            return node.type.createChecked(node.attrs, node.content, newMarks);
        }
    }
    return node;
};
// @see: https://github.com/ProseMirror/prosemirror/issues/710
// @see: https://bugs.chromium.org/p/chromium/issues/detail?id=740085
// Chrome >= 58 (desktop only)
var isChromeWithSelectionBug = browser.chrome && !browser.android && browser.chrome_version >= 58;

/**
 * Method extracted from typeahed plugin to be shared with the element browser on handling element insertion.
 */
var insertSelectedItem = function (maybeNode, opts) {
    if (opts === void 0) { opts = {}; }
    return function (state, tr, start) {
        if (!maybeNode) {
            return tr;
        }
        var isInputFragment = maybeNode instanceof Fragment;
        var node;
        try {
            node =
                maybeNode instanceof Node || isInputFragment
                    ? maybeNode
                    : typeof maybeNode === 'string'
                        ? state.schema.text(maybeNode)
                        : Node.fromJSON(state.schema, maybeNode);
        }
        catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            return tr;
        }
        if (node.isText) {
            tr = tr.replaceWith(start, start, node);
            /**
             *
             * Replacing a type ahead query mark with a block node.
             *
             */
        }
        else if (node.isBlock) {
            tr = safeInsert(normaliseNestedLayout(state, node), undefined, true)(tr);
            /**
             *
             * Replacing a type ahead query mark with an inline node.
             *
             */
        }
        else if (node.isInline || isInputFragment) {
            var fragment = isInputFragment
                ? node
                : Fragment.fromArray([node, state.schema.text(' ')]);
            tr = tr.replaceWith(start, start, fragment);
            // This problem affects Chrome v58+. See: https://github.com/ProseMirror/prosemirror/issues/710
            if (isChromeWithSelectionBug) {
                var selection = document.getSelection();
                if (selection) {
                    selection.empty();
                }
            }
            if (opts.selectInlineNode) {
                // Select inserted node
                tr = tr.setSelection(NodeSelection.create(tr.doc, start));
            }
            else {
                // Placing cursor after node + space.
                tr = tr.setSelection(Selection.near(tr.doc.resolve(start + fragment.size)));
            }
        }
        return tr;
    };
};

var selectCurrentItem = function (mode) {
    if (mode === void 0) { mode = 'selected'; }
    return function (state, dispatch) {
        var _a = pluginKey$1.getState(state), active = _a.active, currentIndex = _a.currentIndex, items = _a.items, typeAheadHandler = _a.typeAheadHandler;
        if (!active || !typeAheadHandler) {
            return false;
        }
        if (!typeAheadHandler.selectItem || !items[currentIndex]) {
            return withTypeAheadQueryMarkPosition(state, function (start, end) {
                return insertFallbackCommand(start, end)(state, dispatch);
            });
        }
        return selectItem(typeAheadHandler, items[currentIndex], mode)(state, dispatch);
    };
};
var selectSingleItemOrDismiss = function (mode) {
    if (mode === void 0) { mode = 'selected'; }
    return function (state, dispatch) {
        var _a = pluginKey$1.getState(state), active = _a.active, items = _a.items, typeAheadHandler = _a.typeAheadHandler;
        if (!active || !typeAheadHandler || !typeAheadHandler.selectItem) {
            return false;
        }
        if (items.length === 1) {
            return selectItem(typeAheadHandler, items[0], mode)(state, dispatch);
        }
        if (!items || items.length === 0) {
            dismissCommand()(state, dispatch);
            return false;
        }
        return false;
    };
};
var selectByIndex = function (index) { return function (state, dispatch) {
    var _a = pluginKey$1.getState(state), active = _a.active, items = _a.items, typeAheadHandler = _a.typeAheadHandler;
    if (!active ||
        !typeAheadHandler ||
        !typeAheadHandler.selectItem ||
        !items[index]) {
        return false;
    }
    return selectItem(typeAheadHandler, items[index])(state, dispatch);
}; };
var selectItem = function (handler, item, mode) {
    if (mode === void 0) { mode = 'selected'; }
    return function (state, dispatch) {
        return withTypeAheadQueryMarkPosition(state, function (start, end) {
            var insert = function (maybeNode, opts) {
                if (opts === void 0) { opts = {}; }
                var tr = state.tr;
                tr = tr
                    .setMeta(pluginKey$1, { action: ACTIONS.SELECT_CURRENT })
                    .replaceWith(start, end, Fragment.empty);
                return insertSelectedItem(maybeNode, opts)(state, tr, start);
            };
            var tr = handler.selectItem(state, item, insert, { mode: mode });
            // if this is the invite teammates item, delete the trigger and query
            // if (isInviteItem(item.mention)) {
            //   // allow the user the select the invite item by left click or enter, but not by space bar
            //   if (mode !== 'space') {
            //     if (dispatch) {
            //       dispatch(state.tr.delete(start, end));
            //       return true;
            //     }
            //   }
            //   return false;
            // }
            if (tr === false) {
                return insertFallbackCommand(start, end)(state, dispatch);
            }
            if (dispatch) {
                dispatch(tr);
            }
            return true;
        });
    };
};
var insertFallbackCommand = function (start, end) { return function (state, dispatch) {
    var _a = pluginKey$1.getState(state), query = _a.query, trigger = _a.trigger;
    var node = state.schema.text(trigger + query);
    if (dispatch) {
        dispatch(state.tr.replaceWith(start, end, node));
    }
    return true;
}; };
var withTypeAheadQueryMarkPosition = function (state, cb) {
    var queryMark = findTypeAheadQuery(state);
    if (!queryMark || queryMark.start === -1) {
        return false;
    }
    return cb(queryMark.start, queryMark.end);
};

function isQueryActive(mark, doc, from, to) {
    var active = false;
    doc.nodesBetween(from, to, function (node) {
        if (!active && mark.isInSet(node.marks)) {
            active = true;
        }
    });
    return active;
}

var updateQueryCommand = function (query) { return function (state, dispatch) {
    var queryMark = findTypeAheadQuery(state);
    var activeQuery = isQueryActive(state.schema.marks.typeAheadQuery, state.doc, state.selection.from, state.selection.to);
    if (queryMark === null || activeQuery === false) {
        return false;
    }
    if (dispatch) {
        dispatch(state.tr.setMeta(pluginKey$1, {
            action: ACTIONS.SET_QUERY,
            params: { query: query },
        }));
    }
    return true;
}; };

function createInitialPluginState(prevActiveState, isAllowed) {
    if (prevActiveState === void 0) { prevActiveState = false; }
    if (isAllowed === void 0) { isAllowed = true; }
    return {
        isAllowed: isAllowed,
        active: false,
        prevActiveState: prevActiveState,
        query: null,
        trigger: null,
        typeAheadHandler: null,
        currentIndex: 0,
        items: [],
        itemsLoader: null,
        queryMarkPos: null,
        queryStarted: 0,
        upKeyCount: 0,
        downKeyCount: 0,
    };
}
function createPlugin(dispatch, typeAhead) {
    return new Plugin({
        key: pluginKey$1,
        state: {
            init: function () {
                return createInitialPluginState();
            },
            apply: function (tr, pluginState, _oldState, state) {
                var meta = tr.getMeta(pluginKey$1) || {};
                var action = meta.action, params = meta.params;
                switch (action) {
                    case ACTIONS.SET_CURRENT_INDEX:
                        return setCurrentItemIndex({
                            dispatch: dispatch,
                            pluginState: pluginState,
                            tr: tr,
                            params: params,
                        });
                    case ACTIONS.SELECT_PREV:
                        return selectPrevActionHandler({ dispatch: dispatch, pluginState: pluginState, tr: tr });
                    case ACTIONS.SELECT_NEXT:
                        return selectNextActionHandler({ dispatch: dispatch, pluginState: pluginState, tr: tr });
                    case ACTIONS.ITEMS_LIST_UPDATED:
                        return itemsListUpdatedActionHandler({ dispatch: dispatch, pluginState: pluginState, tr: tr });
                    case ACTIONS.SELECT_CURRENT:
                        var _a = tr.selection, from = _a.from, to = _a.to;
                        var typeAheadQuery = state.schema.marks.typeAheadQuery;
                        // If inserted content has typeAheadQuery mark should fallback to default action handler
                        return tr.doc.rangeHasMark(from - 1, to, typeAheadQuery)
                            ? defaultActionHandler({
                                dispatch: dispatch,
                                typeAhead: typeAhead,
                                state: state,
                                pluginState: pluginState,
                                tr: tr,
                            })
                            : selectCurrentActionHandler({ dispatch: dispatch, pluginState: pluginState, tr: tr });
                    case ACTIONS.SET_QUERY:
                        return updateQueryHandler({ dispatch: dispatch, pluginState: pluginState, tr: tr, params: params });
                    default:
                        return defaultActionHandler({
                            dispatch: dispatch,
                            typeAhead: typeAhead,
                            state: state,
                            pluginState: pluginState,
                            tr: tr,
                        });
                }
            },
        },
        view: function () {
            return {
                update: function (editorView) {
                    var pluginState = pluginKey$1.getState(editorView.state);
                    if (!pluginState) {
                        return;
                    }
                    var state = editorView.state, dispatch = editorView.dispatch;
                    var doc = state.doc, selection = state.selection;
                    var from = selection.from, to = selection.to;
                    var typeAheadQuery = state.schema.marks.typeAheadQuery;
                    // Disable type ahead query when removing trigger.
                    if (pluginState.active &&
                        !pluginState.query &&
                        !pluginState.trigger) {
                        dismissCommand()(state, dispatch);
                        return;
                    }
                    // Disable type ahead query when the first character is a space.
                    if (pluginState.active &&
                        (pluginState.query || '').indexOf(' ') === 0) {
                        dismissCommand()(state, dispatch);
                        return;
                    }
                    // Optimization to not call dismissCommand if plugin is in an inactive state.
                    if (!pluginState.active &&
                        pluginState.prevActiveState &&
                        !doc.rangeHasMark(from - 1, to, typeAheadQuery)) {
                        dismissCommand()(state, dispatch);
                        return;
                    }
                    // Fetch type ahead items if handler returned a promise.
                    if (pluginState.active && pluginState.itemsLoader) {
                        pluginState.itemsLoader.promise.then(function (items) {
                            return itemsListUpdated(items)(editorView.state, dispatch);
                        });
                    }
                },
            };
        },
        appendTransaction: function (_trs, _oldState, newState) {
            var pluginState = pluginKey$1.getState(newState);
            if (pluginState.active &&
                pluginState.query &&
                pluginState.typeAheadHandler &&
                pluginState.typeAheadHandler.forceSelect &&
                pluginState.typeAheadHandler.forceSelect(pluginState.query, pluginState.items)) {
                var newTr_1;
                selectCurrentItem()(newState, function (tr) { return (newTr_1 = tr); });
                return newTr_1;
            }
            return null;
        },
        props: {
            handleDOMEvents: {
                input: function (view, event) {
                    var state = view.state, dispatch = view.dispatch;
                    var selection = state.selection, schema = state.schema;
                    if (selection instanceof TextSelection &&
                        selection.$cursor &&
                        schema.marks.typeAheadQuery.isInSet(selection.$cursor.marks())) {
                        updateQueryCommand(event.data)(state, dispatch);
                        return false;
                    }
                    return false;
                },
                // FM-2123: On latest Android version Q there's a bug while compositionend,
                // the typeAheadQuery is inserted next to the position of the trigger character (so that creates double characters).
                // In this use case, need to replace the last written character with our typeAheadQuery.
                compositionend: function (view, event) {
                    var state = view.state, dispatch = view.dispatch;
                    var selection = state.selection, schema = state.schema;
                    var triggers = typeAhead.map(function (typeAheadHandler) { return typeAheadHandler.trigger; });
                    if (triggers.indexOf(event.data) !== -1 &&
                        selection instanceof TextSelection &&
                        selection.$cursor &&
                        !schema.marks.typeAheadQuery.isInSet(selection.$cursor.marks())) {
                        insertTypeAheadQuery(event.data, true)(state, dispatch);
                        return true;
                    }
                    return false;
                },
            },
        },
    });
}
function createItemsLoader(promiseOfItems) {
    var canceled = false;
    return {
        promise: new Promise(function (resolve, reject) {
            promiseOfItems
                .then(function (result) { return !canceled && resolve(result); })
                .catch(function (error) { return !canceled && reject(error); });
        }),
        cancel: function () {
            canceled = true;
        },
    };
}
function defaultActionHandler(_a) {
    var dispatch = _a.dispatch, typeAhead = _a.typeAhead, pluginState = _a.pluginState, state = _a.state, tr = _a.tr;
    var typeAheadQuery = state.schema.marks.typeAheadQuery;
    var doc = state.doc, selection = state.selection;
    var from = selection.from, to = selection.to;
    var isActive = isQueryActive(typeAheadQuery, doc, from - 1, to);
    var isAllowed = isMarkTypeAllowedInCurrentSelection(typeAheadQuery, state);
    if (!isAllowed && !isActive) {
        if (pluginState &&
            pluginState.active === isActive &&
            pluginState.isAllowed === isAllowed) {
            return pluginState;
        }
        var newPluginState_1 = createInitialPluginState(pluginState.active, isAllowed);
        dispatch(pluginKey$1, newPluginState_1);
        return newPluginState_1;
    }
    var nodeBefore = selection.$from.nodeBefore;
    if (!isActive || !nodeBefore || !pluginState) {
        var newPluginState_2 = createInitialPluginState(pluginState ? pluginState.active : false);
        if (!pluginState || pluginState.active || !pluginState.isAllowed) {
            dispatch(pluginKey$1, newPluginState_2);
        }
        return newPluginState_2;
    }
    var typeAheadMark = typeAheadQuery.isInSet(nodeBefore.marks || []);
    if (!typeAheadMark || !typeAheadMark.attrs.trigger) {
        return pluginState;
    }
    var textContent = nodeBefore.textContent || '';
    var trigger = typeAheadMark.attrs.trigger.replace(/([^\x00-\xFF]|[\s\n])+/g, '');
    // If trigger has been removed, reset plugin state
    if (!textContent.includes(trigger)) {
        var newPluginState_3 = __assign(__assign({}, createInitialPluginState(true)), { active: true });
        dispatch(pluginKey$1, newPluginState_3);
        return newPluginState_3;
    }
    var query = textContent
        .replace(/^([^\x00-\xFF]|[\s\n])+/g, '')
        .replace(trigger, '');
    var typeAheadHandler = typeAhead.find(function (t) { return t.trigger === trigger; });
    var typeAheadItems = [];
    var itemsLoader = null;
    var highlight = null;
    try {
        var intl = { locale: 'en' };
        typeAheadItems = typeAheadHandler.getItems(query, state, 
        // intl,
        {
            prevActive: pluginState.prevActiveState,
            queryChanged: query !== pluginState.query || trigger !== pluginState.trigger,
        }, tr, dispatch);
        if (typeAheadHandler.getHighlight) {
            highlight = typeAheadHandler.getHighlight(state);
        }
        if (pluginState.itemsLoader) {
            pluginState.itemsLoader.cancel();
        }
        if (!Array.isArray(typeAheadItems)) {
            itemsLoader = createItemsLoader(typeAheadItems);
            typeAheadItems = pluginState.items;
        }
    }
    catch (e) { }
    var queryMark = findTypeAheadQuery(state);
    var newPluginState = {
        isAllowed: isAllowed,
        query: query,
        trigger: trigger,
        typeAheadHandler: typeAheadHandler,
        active: true,
        prevActiveState: pluginState.active,
        items: typeAheadItems,
        itemsLoader: itemsLoader,
        currentIndex: pluginState.currentIndex,
        queryMarkPos: queryMark !== null ? queryMark.start : null,
        queryStarted: Date.now(),
        upKeyCount: 0,
        downKeyCount: 0,
        highlight: highlight,
    };
    dispatch(pluginKey$1, newPluginState);
    return newPluginState;
}
function setCurrentItemIndex(_a) {
    var dispatch = _a.dispatch, pluginState = _a.pluginState, params = _a.params;
    if (!params) {
        return pluginState;
    }
    var newPluginState = __assign(__assign({}, pluginState), { currentIndex: params.currentIndex || params.currentIndex === 0
            ? params.currentIndex
            : pluginState.currentIndex });
    dispatch(pluginKey$1, newPluginState);
    return newPluginState;
}
function updateQueryHandler(_a) {
    var dispatch = _a.dispatch, pluginState = _a.pluginState, params = _a.params;
    if (!params) {
        return pluginState;
    }
    var newPluginState = __assign(__assign({}, pluginState), { query: typeof params.query === 'string' ? params.query : null });
    dispatch(pluginKey$1, newPluginState);
    return newPluginState;
}
function selectPrevActionHandler(_a) {
    var dispatch = _a.dispatch, pluginState = _a.pluginState;
    var newIndex = pluginState.currentIndex - 1;
    var newPluginState = __assign(__assign({}, pluginState), { currentIndex: newIndex < 0 ? pluginState.items.length - 1 : newIndex, upKeyCount: ++pluginState.upKeyCount });
    dispatch(pluginKey$1, newPluginState);
    return newPluginState;
}
function selectNextActionHandler(_a) {
    var dispatch = _a.dispatch, pluginState = _a.pluginState;
    var newIndex = pluginState.currentIndex + 1;
    var newPluginState = __assign(__assign({}, pluginState), { currentIndex: newIndex > pluginState.items.length - 1 ? 0 : newIndex, downKeyCount: ++pluginState.downKeyCount });
    dispatch(pluginKey$1, newPluginState);
    return newPluginState;
}
function itemsListUpdatedActionHandler(_a) {
    var dispatch = _a.dispatch, pluginState = _a.pluginState, tr = _a.tr;
    var items = tr.getMeta(pluginKey$1).items;
    var newPluginState = __assign(__assign({}, pluginState), { items: items, itemsLoader: null, 
        // Set to 0 to always reset the query to the top of the typeahead
        currentIndex: 0 });
    dispatch(pluginKey$1, newPluginState);
    return newPluginState;
}
function selectCurrentActionHandler(_a) {
    var dispatch = _a.dispatch;
    var newPluginState = createInitialPluginState(false);
    dispatch(pluginKey$1, newPluginState);
    return newPluginState;
}

// We cannot set a proper plugin key on input rule plugins, so instead, once
// the plugin is created we save its key to this variable
var typeAheadInputRulesPluginKey = '';
function inputRulePlugin(schema, typeAheads) {
    var triggersRegex = typeAheads
        .map(function (t) { return t.customRegex || t.trigger; })
        .join('|');
    if (!triggersRegex.length) {
        return;
    }
    var regex = new RegExp("(^|[.!?\\s" + leafNodeReplacementCharacter + "])(" + triggersRegex + ")$");
    var typeAheadInputRule = createInputRule(regex, function (state, match) {
        var typeAheadState = pluginKey$1.getState(state);
        /**
         * Why using match 2 and 3?  Regex:
         * (allowed characters before trigger)(joined|triggers|(sub capture groups))
         *            match[1]                     match[2]          match[3] – optional
         */
        var trigger = match[3] || match[2];
        if (!typeAheadState.isAllowed || !trigger) {
            return null;
        }
        var mark = schema.mark('typeAheadQuery', { trigger: trigger });
        var tr = state.tr, selection = state.selection;
        var marks = selection.$from.marks();
        return tr.replaceSelectionWith(schema.text(trigger, __spreadArrays([mark], marks)), false);
    });
    var plugin = instrumentedInputRule('type-ahead', {
        rules: [typeAheadInputRule],
    });
    typeAheadInputRulesPluginKey = plugin.key;
    return plugin;
}

function keymapPlugin$1() {
    var list = {};
    bindKeymapWithCommand(enter.common, function (state, dispatch) {
        var pluginState = pluginKey$1.getState(state);
        if (!pluginState || !pluginState.active) {
            return false;
        }
        return selectCurrentItem('enter')(state, dispatch);
    }, list);
    bindKeymapWithCommand(moveUp.common, function (state, dispatch) {
        var pluginState = pluginKey$1.getState(state);
        if (!pluginState || !pluginState.active) {
            return false;
        }
        if (dispatch) {
            dispatch(state.tr.setMeta(pluginKey$1, { action: ACTIONS.SELECT_PREV }));
        }
        return true;
    }, list);
    bindKeymapWithCommand(moveDown.common, function (state, dispatch) {
        var pluginState = pluginKey$1.getState(state);
        if (!pluginState || !pluginState.active) {
            return false;
        }
        if (dispatch) {
            dispatch(state.tr.setMeta(pluginKey$1, { action: ACTIONS.SELECT_NEXT }));
        }
        return true;
    }, list);
    bindKeymapWithCommand(insertNewLine.common, function (state, dispatch) {
        var pluginState = pluginKey$1.getState(state);
        if (!pluginState || !pluginState.active) {
            return false;
        }
        return selectCurrentItem('shift-enter')(state, dispatch);
    }, list);
    bindKeymapWithCommand(tab.common, function (state, dispatch) {
        var pluginState = pluginKey$1.getState(state);
        if (!pluginState || !pluginState.active) {
            return false;
        }
        return selectCurrentItem('tab')(state, dispatch);
    }, list);
    bindKeymapWithCommand(escape.common, function (state, dispatch) {
        var pluginState = pluginKey$1.getState(state);
        if (!pluginState || !pluginState.active) {
            return false;
        }
        /**
         * Jira uses escape to toggle the collapsed editor
         * stop the event propagation when the picker is open
         */
        if (window.event) {
            window.event.stopPropagation();
        }
        return dismissCommand()(state, dispatch);
    }, list);
    bindKeymapWithCommand(space.common, function (state, dispatch) {
        var pluginState = pluginKey$1.getState(state);
        if (pluginState && pluginState.active) {
            return selectSingleItemOrDismiss('space')(state, dispatch);
        }
        return false;
    }, list);
    return keymap(list);
}

function isBody(elem) {
    return elem === document.body;
}
function isTextNode(elem) {
    return elem && elem.nodeType === 3;
}
/**
 * Decides if given fitHeight fits below or above the target taking boundaries into account.
 */
function getVerticalPlacement(target, boundariesElement, fitHeight, alignY, forcePlacement) {
    if (forcePlacement && alignY) {
        return alignY;
    }
    if (!fitHeight) {
        return 'bottom';
    }
    if (isTextNode(target)) {
        target = target.parentElement;
    }
    var boundariesClientRect = boundariesElement.getBoundingClientRect();
    var boundariesHeight = boundariesClientRect.height;
    var boundariesTop = isBody(boundariesElement)
        ? 0
        : boundariesClientRect.top;
    var _a = target.getBoundingClientRect(), targetTop = _a.top, targetHeight = _a.height;
    var spaceAbove = targetTop - (boundariesTop - boundariesElement.scrollTop);
    var spaceBelow = boundariesTop + boundariesHeight - (targetTop + targetHeight);
    if (spaceBelow >= fitHeight || spaceBelow >= spaceAbove) {
        return 'bottom';
    }
    return 'top';
}
/**
 * Decides if given fitWidth fits to the left or to the right of the target taking boundaries into account.
 */
function getHorizontalPlacement(target, boundariesElement, fitWidth, alignX, forcePlacement) {
    if (forcePlacement && alignX) {
        return alignX;
    }
    if (!fitWidth) {
        return alignX || 'left';
    }
    if (isTextNode(target)) {
        target = target.parentElement;
    }
    var _a = target.getBoundingClientRect(), targetLeft = _a.left, targetWidth = _a.width;
    var _b = boundariesElement.getBoundingClientRect(), boundariesLeft = _b.left, boundariesWidth = _b.width;
    var spaceLeft = targetLeft - boundariesLeft + targetWidth;
    var spaceRight = boundariesLeft + boundariesWidth - targetLeft;
    if (alignX && spaceLeft > fitWidth && spaceRight > fitWidth) {
        return alignX;
    }
    else if (spaceRight >= fitWidth || (spaceRight >= spaceLeft && !alignX)) {
        return 'left';
    }
    return 'right';
}
function calculatePlacement(target, boundariesElement, fitWidth, fitHeight, alignX, alignY, forcePlacement) {
    return [
        getVerticalPlacement(target, boundariesElement, fitHeight, alignY, forcePlacement),
        getHorizontalPlacement(target, boundariesElement, fitWidth, alignX, forcePlacement),
    ];
}
var calculateHorizontalPlacement = function (_a) {
    var placement = _a.placement, targetLeft = _a.targetLeft, targetRight = _a.targetRight, targetWidth = _a.targetWidth, isPopupParentBody = _a.isPopupParentBody, popupOffsetParentLeft = _a.popupOffsetParentLeft, popupOffsetParentRight = _a.popupOffsetParentRight, popupOffsetParentScrollLeft = _a.popupOffsetParentScrollLeft, popupOffsetParentClientWidth = _a.popupOffsetParentClientWidth, popupClientWidth = _a.popupClientWidth, offset = _a.offset, _b = _a.allowOutOfBounds, allowOutOfBounds = _b === void 0 ? false : _b;
    var position = {};
    if (placement === 'left') {
        position.left = Math.ceil(targetLeft -
            popupOffsetParentLeft +
            (isPopupParentBody ? 0 : popupOffsetParentScrollLeft) +
            offset[0]);
    }
    else if (placement === 'center') {
        position.left = Math.ceil(targetLeft -
            popupOffsetParentLeft +
            (isPopupParentBody ? 0 : popupOffsetParentScrollLeft) +
            offset[0] +
            targetWidth / 2 -
            popupClientWidth / 2);
    }
    else if (placement === 'end') {
        var left = Math.ceil(targetRight -
            popupOffsetParentLeft +
            (isPopupParentBody ? 0 : popupOffsetParentScrollLeft) +
            offset[0]);
        position.left = left;
    }
    else {
        position.right = Math.ceil(popupOffsetParentRight -
            targetRight -
            (isPopupParentBody ? 0 : popupOffsetParentScrollLeft) +
            offset[0]);
    }
    if (!allowOutOfBounds) {
        if (position.left !== undefined) {
            position.left = getPopupXInsideParent(position.left, popupClientWidth, popupOffsetParentClientWidth);
        }
        if (position.right !== undefined) {
            position.right = getPopupXInsideParent(position.right, popupClientWidth, popupOffsetParentClientWidth);
        }
    }
    return position;
};
var getPopupXInsideParent = function (x, popupClientWidth, popupOffsetParentClientWidth) {
    // minimum distance the popup can be from the edge of its parent
    var minPopupMargin = 1;
    // prevent going too far right
    if (popupOffsetParentClientWidth < x + popupClientWidth) {
        x = popupOffsetParentClientWidth - popupClientWidth - minPopupMargin;
    }
    // prevent going too far left
    return Math.max(minPopupMargin, x);
};
var calculateVerticalStickBottom = function (_a) {
    var target = _a.target, targetTop = _a.targetTop, targetHeight = _a.targetHeight, popup = _a.popup, offset = _a.offset, position = _a.position;
    var scrollParent = findOverflowScrollParent(target);
    var newPos = __assign({}, position);
    if (scrollParent) {
        var topOffsetTop = targetTop - scrollParent.getBoundingClientRect().top;
        var targetEnd = targetHeight + topOffsetTop;
        if (scrollParent.clientHeight - targetEnd <=
            popup.clientHeight + offset[1] * 2 &&
            topOffsetTop < scrollParent.clientHeight) {
            var scroll_1 = targetEnd - scrollParent.clientHeight + offset[1] * 2;
            var top_1 = newPos.top || 0;
            top_1 = top_1 - (scroll_1 + popup.clientHeight);
            newPos.top = top_1;
        }
    }
    return newPos;
};
var calculateVerticalStickTop = function (_a) {
    var target = _a.target, targetTop = _a.targetTop, targetHeight = _a.targetHeight, popupOffsetParentHeight = _a.popupOffsetParentHeight, popupOffsetParent = _a.popupOffsetParent, offset = _a.offset, position = _a.position, placement = _a.placement;
    var scrollParent = findOverflowScrollParent(target);
    var newPos = __assign({}, position);
    if (scrollParent) {
        var scrollParentTop = scrollParent.getBoundingClientRect().top;
        var topBoundary = targetTop - scrollParentTop;
        var scrollParentScrollTop = scrollParent.scrollTop;
        if (topBoundary < 0) {
            var isBelowNodeBoundary = targetTop +
                (scrollParentScrollTop - scrollParentTop) +
                targetHeight +
                offset[1] <
                scrollParentScrollTop;
            if (placement === 'top') {
                if (isBelowNodeBoundary) {
                    newPos.bottom =
                        popupOffsetParentHeight -
                            (topBoundary + popupOffsetParent.scrollTop + targetHeight);
                }
                else {
                    newPos.bottom = topBoundary + (newPos.bottom || 0);
                }
            }
            if (placement === 'start') {
                if (isBelowNodeBoundary) {
                    newPos.top = topBoundary + popupOffsetParent.scrollTop + targetHeight;
                }
                else {
                    newPos.top = Math.abs(topBoundary) + (newPos.top || 0) + offset[1];
                }
            }
        }
    }
    return newPos;
};
var calculateVerticalPlacement = function (_a) {
    var placement = _a.placement, targetTop = _a.targetTop, targetHeight = _a.targetHeight, isPopupParentBody = _a.isPopupParentBody, popupOffsetParentHeight = _a.popupOffsetParentHeight, popupOffsetParentTop = _a.popupOffsetParentTop, popupOffsetParentScrollTop = _a.popupOffsetParentScrollTop, borderBottomWidth = _a.borderBottomWidth, offset = _a.offset;
    var position = {};
    if (placement === 'top') {
        position.bottom = Math.ceil(popupOffsetParentHeight -
            (targetTop - popupOffsetParentTop) -
            (isPopupParentBody ? 0 : popupOffsetParentScrollTop) -
            borderBottomWidth +
            offset[1]);
    }
    else if (placement === 'start') {
        position.top = Math.ceil(targetTop -
            popupOffsetParentTop -
            offset[1] +
            (isPopupParentBody ? 0 : popupOffsetParentScrollTop));
    }
    else {
        var top_2 = Math.ceil(targetTop -
            popupOffsetParentTop +
            targetHeight +
            (isPopupParentBody ? 0 : popupOffsetParentScrollTop) -
            borderBottomWidth +
            offset[1]);
        position.top = top_2;
    }
    return position;
};
/**
 * Calculates relative coordinates for placing popup along with the target.
 * Uses placement from calculatePlacement.
 */
function calculatePosition(_a) {
    var placement = _a.placement, target = _a.target, popup = _a.popup, offset = _a.offset, stick = _a.stick, _b = _a.allowOutOfBounds, allowOutOfBounds = _b === void 0 ? false : _b, rect = _a.rect;
    var position = {};
    if (!target || !popup || !popup.offsetParent) {
        return position;
    }
    if (isTextNode(target)) {
        target = target.parentElement;
    }
    var popupOffsetParent = popup.offsetParent;
    var offsetParentStyle = popupOffsetParent.style;
    var borderBottomWidth = 0;
    if (offsetParentStyle && offsetParentStyle.borderBottomWidth) {
        borderBottomWidth = parseInt(offsetParentStyle.borderBottomWidth, 10);
    }
    var verticalPlacement = placement[0], horizontalPlacement = placement[1];
    var _c = rect ? rect : popupOffsetParent.getBoundingClientRect(), popupOffsetParentTop = _c.top, popupOffsetParentLeft = _c.left, popupOffsetParentRight = _c.right, popupOffsetParentHeight = _c.height;
    var _d = target.getBoundingClientRect(), targetTop = _d.top, targetLeft = _d.left, targetRight = _d.right, targetHeight = _d.height, targetWidth = _d.width;
    var isPopupParentBody = isBody(popupOffsetParent);
    var verticalPosition = calculateVerticalPlacement({
        placement: verticalPlacement,
        targetTop: targetTop,
        isPopupParentBody: isPopupParentBody,
        popupOffsetParentHeight: popupOffsetParentHeight,
        popupOffsetParentTop: popupOffsetParentTop,
        popupOffsetParentScrollTop: popupOffsetParent.scrollTop || 0,
        targetHeight: targetHeight,
        borderBottomWidth: borderBottomWidth,
        offset: offset,
    });
    position = __assign(__assign({}, position), verticalPosition);
    if ((verticalPlacement === 'top' || verticalPlacement === 'start') && stick) {
        position = calculateVerticalStickTop({
            target: target,
            targetTop: targetTop,
            targetHeight: targetHeight,
            popupOffsetParentHeight: popupOffsetParentHeight,
            popupOffsetParent: popupOffsetParent,
            popup: popup,
            offset: offset,
            position: position,
            placement: verticalPlacement,
        });
    }
    if (verticalPlacement !== 'top' && verticalPlacement !== 'start' && stick) {
        position = calculateVerticalStickBottom({
            target: target,
            targetTop: targetTop,
            targetHeight: targetHeight,
            popup: popup,
            offset: offset,
            position: position,
        });
    }
    var horizontalPosition = calculateHorizontalPlacement({
        placement: horizontalPlacement,
        targetLeft: targetLeft,
        targetRight: targetRight,
        targetWidth: targetWidth,
        isPopupParentBody: isPopupParentBody,
        popupOffsetParentLeft: popupOffsetParentLeft,
        popupOffsetParentRight: popupOffsetParentRight,
        popupOffsetParentScrollLeft: popupOffsetParent.scrollLeft || 0,
        popupOffsetParentClientWidth: popup.offsetParent.clientWidth,
        popupClientWidth: popup.clientWidth || 0,
        offset: offset,
        allowOutOfBounds: allowOutOfBounds,
    });
    position = __assign(__assign({}, position), horizontalPosition);
    return position;
}
function validatePosition(popup) {
    // popup.offsetParent does not exist if the popup element is not mounted
    if (!popup || !popup.offsetParent) {
        return false;
    }
    return true;
}
/**
 * Traverse DOM Tree upwards looking for popup parents with "overflow: scroll".
 */
function findOverflowScrollParent(popup) {
    var parent = popup;
    if (!parent) {
        return false;
    }
    while ((parent = parent.parentElement)) {
        // IE11 on Window 8 doesn't show styles from CSS when accessing through element.style property.
        var style = window.getComputedStyle(parent);
        if (style.overflow === 'scroll' ||
            style.overflowX === 'scroll' ||
            style.overflowY === 'scroll' ||
            parent.classList.contains('fabric-editor-popup-scroll-parent')) {
            return parent;
        }
    }
    return false;
}

// import { akEditorFloatingPanelZIndex } from '@atlaskit/editor-shared-styles';
var akEditorFloatingPanelZIndex = 10;
var Popup = /** @class */ (function (_super) {
    __extends(Popup, _super);
    function Popup() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.state = {
            overflowScrollParent: false,
            validPosition: true,
        };
        _this.placement = ['', ''];
        _this.handleRef = function (popup) {
            if (!popup) {
                return;
            }
            _this.initPopup(popup);
        };
        _this.scheduledUpdatePosition = rafSchedule(function (props) {
            return _this.updatePosition(props);
        });
        _this.onResize = function () { return _this.scheduledUpdatePosition(_this.props); };
        return _this;
    }
    /**
     * Calculates new popup position
     */
    Popup.prototype.updatePosition = function (props, state) {
        if (props === void 0) { props = this.props; }
        if (state === void 0) { state = this.state; }
        var target = props.target, fitHeight = props.fitHeight, fitWidth = props.fitWidth, boundariesElement = props.boundariesElement, offset = props.offset, onPositionCalculated = props.onPositionCalculated, onPlacementChanged = props.onPlacementChanged, alignX = props.alignX, alignY = props.alignY, stick = props.stick, forcePlacement = props.forcePlacement, allowOutOfBounds = props.allowOutOfBounds, rect = props.rect;
        var popup = state.popup;
        if (!target || !popup) {
            return;
        }
        var placement = calculatePlacement(target, boundariesElement || document.body, fitWidth, fitHeight, alignX, alignY, forcePlacement);
        if (onPlacementChanged && this.placement.join('') !== placement.join('')) {
            onPlacementChanged(placement);
            this.placement = placement;
        }
        var position = calculatePosition({
            placement: placement,
            popup: popup,
            target: target,
            stick: stick,
            offset: offset,
            allowOutOfBounds: allowOutOfBounds,
            rect: rect,
        });
        position = onPositionCalculated ? onPositionCalculated(position) : position;
        this.setState({
            position: position,
            validPosition: validatePosition(target),
        });
    };
    Popup.prototype.cannotSetPopup = function (popup, target, overflowScrollParent) {
        /**
         * Check whether:
         * 1. Popup's offset targets which means whether or not its possible to correctly position popup along with given target.
         * 2. Popup is inside "overflow: scroll" container, but its offset parent isn't.
         *
         * Currently Popup isn't capable of position itself correctly in case 2,
         * Add "position: relative" to "overflow: scroll" container or to some other FloatingPanel wrapper inside it.
         */
        return (!target ||
            (document.body.contains(target) &&
                popup.offsetParent &&
                !popup.offsetParent.contains(target)) ||
            (overflowScrollParent &&
                !overflowScrollParent.contains(popup.offsetParent)));
    };
    /**
     * Popup initialization.
     * Checks whether it's possible to position popup along given target, and if it's not throws an error.
     */
    Popup.prototype.initPopup = function (popup) {
        var _this = this;
        var target = this.props.target;
        var overflowScrollParent = findOverflowScrollParent(popup);
        if (this.cannotSetPopup(popup, target, overflowScrollParent)) {
            return;
        }
        this.setState({ popup: popup, overflowScrollParent: overflowScrollParent }, function () { return _this.scheduledUpdatePosition(_this.props); });
    };
    Popup.prototype.UNSAFE_componentWillReceiveProps = function (newProps) {
        // We are delaying `updatePosition` otherwise it happens before the children
        // get rendered and we end up with a wrong position
        this.scheduledUpdatePosition(newProps);
    };
    Popup.prototype.componentDidMount = function () {
        window.addEventListener('resize', this.onResize);
        var stick = this.props.stick;
        if (stick) {
            this.scrollElement = findOverflowScrollParent(this.props.target);
        }
        else {
            this.scrollElement = this.props.scrollableElement;
        }
        if (this.scrollElement) {
            this.scrollElement.addEventListener('scroll', this.onResize);
        }
    };
    Popup.prototype.componentWillUnmount = function () {
        window.removeEventListener('resize', this.onResize);
        if (this.scrollElement) {
            this.scrollElement.removeEventListener('scroll', this.onResize);
        }
        this.scheduledUpdatePosition.cancel();
    };
    Popup.prototype.renderPopup = function () {
        var position = this.state.position;
        var shouldRenderPopup = this.props.shouldRenderPopup;
        if (shouldRenderPopup && !shouldRenderPopup(position || {})) {
            return null;
        }
        return (React.createElement("div", { ref: this.handleRef, style: __assign({ position: 'absolute', zIndex: this.props.zIndex || akEditorFloatingPanelZIndex }, position), "aria-label": this.props.ariaLabel || 'Popup', "data-editor-popup": true }, this.props.children));
    };
    Popup.prototype.render = function () {
        var _a = this.props, target = _a.target, mountTo = _a.mountTo;
        var validPosition = this.state.validPosition;
        if (!target || !validPosition) {
            return null;
        }
        if (mountTo) {
            return createPortal(this.renderPopup(), mountTo);
        }
        // Without mountTo property renders popup as is,
        // which means it will be cropped by "overflow: hidden" container.
        return this.renderPopup();
    };
    Popup.defaultProps = {
        offset: [0, 0],
        allowOutOfBound: false,
    };
    return Popup;
}(React.Component));

/*
  These theme values are expressed as functions so that if we decide to make
  them dependent on props in the future, it wouldn't require a significant
  refactor everywhere they are being used.
*/
var borderRadius = function () { return 3; };
var gridSize = function () { return 8; };
var fontSize = function () { return 14; };
var fontSizeSmall = function () { return 11; };
// export const skeletonShimmer = () =>
//   ({
//     css: {
//       backgroundColor: skeletonColor(),
//       animationDuration: '1.5s',
//       animationIterationCount: 'infinite',
//       animationTimingFunction: 'linear',
//       animationDirection: 'alternate',
//     },
//     keyframes: {
//       from: {
//         backgroundColor: skeletonColor(),
//       },
//       to: {
//         backgroundColor: N30A,
//       },
//     },
//   } as const);

// If a generic is used here, props can be inferred never and passed up (even with defaults)
function divide(fn, divisor) {
    return function (props) { return fn(props) / divisor; };
}

var setCurrentIndex = function (currentIndex) { return function (state, dispatch) {
    if (dispatch) {
        dispatch(state.tr.setMeta(pluginKey$1, {
            action: ACTIONS.SET_CURRENT_INDEX,
            params: { currentIndex: currentIndex },
        }));
    }
    return true;
}; };

var themeNamespace = '@atlaskit-shared-theme/item';
// Used for Group titles and Item descriptions
var compactSmallFontSize = 10;
var compactLineHeight = 1.2;
var gridSize$1 = gridSize();
var defaultTheme = {
    afterItemSpacing: {
        compact: gridSize(),
        default: gridSize(),
    },
    beforeItemSpacing: {
        compact: gridSize(),
        default: gridSize(),
    },
    borderRadius: borderRadius(),
    focus: {
        outline: B100,
    },
    height: {
        compact: 0,
        default: 0,
    },
    width: {
        compact: 'auto',
        default: 'auto',
    },
    padding: {
        default: {
            bottom: gridSize() / 2,
            left: gridSize() / 2,
            right: gridSize() / 2,
            top: gridSize() / 2,
        },
        compact: {
            bottom: gridSize(),
            left: gridSize(),
            right: gridSize(),
            top: gridSize(),
        },
    },
    default: {
        background: background,
        text: text$1,
        secondaryText: N200,
    },
    selected: {
        background: backgroundActive,
        text: N500,
        secondaryText: N200,
    },
    active: {
        background: backgroundActive,
        text: textActive,
        secondaryText: N200,
    },
    hover: {
        background: backgroundHover,
        text: textHover,
        secondaryText: N200,
    },
    disabled: {
        background: N0,
        text: N200,
        secondaryText: N200,
    },
    // same as hover in this case
    dragging: {
        background: N20,
        text: N800,
        secondaryText: N200,
    },
};
var isValidCssValue = function (value) {
    return value !== undefined && value !== null && value !== '';
};
// Returns the theme that contains the requested theme key(s), preferring the user-supplied
// theme if it is provided.
var themeWithKeys = function (maybeTheme, key, parentKey) {
    if (parentKey) {
        return maybeTheme &&
            maybeTheme[parentKey] &&
            isValidCssValue(maybeTheme[parentKey][key])
            ? maybeTheme
            : defaultTheme;
    }
    return maybeTheme && isValidCssValue(maybeTheme[key])
        ? maybeTheme
        : defaultTheme;
};
// Returns the theme value for the requested key(s), falling back to the default theme if the
// user-supplied theme doesn't exist or doesn't contain the requested key(s)
var getThemeStyle = function (maybeTheme, key, parentKey) {
    var theme = themeWithKeys(maybeTheme, key, parentKey);
    return parentKey ? theme[parentKey][key] : theme[key];
};

var getItemState = function (stateName) { return function (_a) {
    var theme = _a.theme;
    var stateStyles = getThemeStyle(theme[themeNamespace], stateName);
    return css(templateObject_1$2 || (templateObject_1$2 = __makeTemplateObject(["\n    background-color: ", ";\n    color: ", ";\n    fill: ", ";\n    text-decoration: none;\n\n    &:focus {\n      color: ", ";\n    }\n  "], ["\n    background-color: ", ";\n    color: ", ";\n    fill: ", ";\n    text-decoration: none;\n\n    &:focus {\n      color: ", ";\n    }\n  "])), stateStyles.background, stateStyles.text, stateStyles.background, stateStyles.text);
}; };
var getPadding = function (_a) {
    var isCompact = _a.isCompact, theme = _a.theme, description = _a.description;
    var paddingKey = isCompact ? 'compact' : 'default';
    var _b = getThemeStyle(theme[themeNamespace], paddingKey, 'padding'), _c = _b.bottom, bottom = _c === void 0 ? 0 : _c, _d = _b.left, left = _d === void 0 ? 0 : _d, _e = _b.right, right = _e === void 0 ? 0 : _e, _f = _b.top, top = _f === void 0 ? 0 : _f;
    var adjustedTop = typeof top === 'function' ? top() : top;
    var adjustedBottom = typeof bottom === 'function' ? bottom() : bottom;
    // Subtract the 1px padding-bottom added to the content and description elements
    // to maintain original height
    adjustedTop = Math.max(description ? adjustedTop - 1 : adjustedTop, 0);
    adjustedBottom = Math.max(adjustedBottom - 1, 0);
    return css(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n    padding: ", "px ", "px ", "px ", "px;\n  "], ["\n    padding: ", "px ", "px ", "px ", "px;\n  "])), adjustedTop, right, adjustedBottom, left);
};
var getHeightStyles = function (_a) {
    var isCompact = _a.isCompact, theme = _a.theme;
    var heightKey = isCompact ? 'compact' : 'default';
    var height = getThemeStyle(theme[themeNamespace], heightKey, 'height');
    return height
        ? css(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n        height: ", "px;\n      "], ["\n        height: ", "px;\n      "])), height) : '';
};
var getWidthStyles = function (_a) {
    var isCompact = _a.isCompact, theme = _a.theme;
    var widthKey = isCompact ? 'compact' : 'default';
    var width = getThemeStyle(theme[themeNamespace], widthKey, 'width');
    if (!width) {
        return '';
    }
    return typeof width === 'number'
        ? css(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n        width: ", "px;\n      "], ["\n        width: ", "px;\n      "])), width) : css(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n        width: ", ";\n      "], ["\n        width: ", ";\n      "])), width);
};
var getInteractiveStyles = function (_a) {
    var theme = _a.theme, isDisabled = _a.isDisabled, isDragging = _a.isDragging, isSelected = _a.isSelected;
    if (isDragging) {
        return css(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n      ", " box-shadow: 0 4px 8px -2px ", ",\n        0 0 1px ", ";\n    "], ["\n      ",
            " box-shadow: 0 4px 8px -2px ", ",\n        0 0 1px ", ";\n    "])), getItemState('dragging'), N60A, N60A);
    }
    var standardFocus = css(templateObject_7 || (templateObject_7 = __makeTemplateObject(["\n    &:focus {\n      box-shadow: 0 0 0 2px\n        ", " inset;\n      text-decoration: none;\n    }\n  "], ["\n    &:focus {\n      box-shadow: 0 0 0 2px\n        ", " inset;\n      text-decoration: none;\n    }\n  "])), getThemeStyle(theme[themeNamespace], 'outline', 'focus'));
    if (isDisabled) {
        return css(templateObject_8 || (templateObject_8 = __makeTemplateObject(["\n      cursor: not-allowed;\n      ", "\n      ", ";\n    "], ["\n      cursor: not-allowed;\n      ", "\n      ", ";\n    "])), getItemState('disabled'), standardFocus);
    }
    if (isSelected) {
        return css(templateObject_9 || (templateObject_9 = __makeTemplateObject(["\n      ", " &:hover {\n        ", ";\n      }\n\n      &:active {\n        ", ";\n      }\n\n      ", ";\n    "], ["\n      ", " &:hover {\n        ", ";\n      }\n\n      &:active {\n        ", ";\n      }\n\n      ", ";\n    "])), getItemState('selected'), getItemState('hover'), getItemState('active'), standardFocus);
    }
    return css(templateObject_10 || (templateObject_10 = __makeTemplateObject(["\n    &:hover {\n      ", ";\n    }\n\n    &:active {\n      ", ";\n    }\n\n    ", ";\n  "], ["\n    &:hover {\n      ", ";\n    }\n\n    &:active {\n      ", ";\n    }\n\n    ", ";\n  "])), getItemState('hover'), getItemState('active'), standardFocus);
};
var ItemBase = function (_a) {
    var theme = _a.theme;
    return css(templateObject_11 || (templateObject_11 = __makeTemplateObject(["\n  && {\n    align-items: center;\n    border-radius: ", "px;\n    box-sizing: border-box;\n    cursor: pointer;\n    display: ", ";\n    flex: none;\n    ", " ", " ", " ", " ", " &:focus {\n      /* focus shadow drawn by getInteractiveStyles */\n\n      outline: none;\n      /* relative position prevents bgcolor of a hovered element from\n      obfuscating focus ring of a focused sibling element */\n      position: relative;\n    }\n  }\n"], ["\n  && {\n    align-items: center;\n    border-radius: ", "px;\n    box-sizing: border-box;\n    cursor: pointer;\n    display: ", ";\n    flex: none;\n    ",
        " ", " ", " ", " ", " &:focus {\n      /* focus shadow drawn by getInteractiveStyles */\n\n      outline: none;\n      /* relative position prevents bgcolor of a hovered element from\n      obfuscating focus ring of a focused sibling element */\n      position: relative;\n    }\n  }\n"])), getThemeStyle(theme[themeNamespace], 'borderRadius'), function (_a) {
        var isHidden = _a.isHidden;
        return (isHidden ? 'none' : 'flex');
    }, getItemState('default'), getPadding, getInteractiveStyles, getHeightStyles, getWidthStyles);
};
var styledRootElement = function (_a) {
    var href = _a.href, linkComponent = _a.linkComponent;
    if (linkComponent) {
        return styled(linkComponent)(templateObject_12 || (templateObject_12 = __makeTemplateObject(["\n      ", ";\n    "], ["\n      ", ";\n    "])), ItemBase);
    }
    if (href) {
        return styled.a(templateObject_13 || (templateObject_13 = __makeTemplateObject(["\n      ", ";\n    "], ["\n      ", ";\n    "])), ItemBase);
    }
    return styled.span(templateObject_14 || (templateObject_14 = __makeTemplateObject(["\n    ", ";\n  "], ["\n    ", ";\n  "])), ItemBase);
};
var templateObject_1$2, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6, templateObject_7, templateObject_8, templateObject_9, templateObject_10, templateObject_11, templateObject_12, templateObject_13, templateObject_14;

// Checkbox/Radio wrapper -- sits left of the children
var InputWrapper = styled.span(templateObject_1$3 || (templateObject_1$3 = __makeTemplateObject(["\n  display: flex;\n  margin: 0 2px;\n"], ["\n  display: flex;\n  margin: 0 2px;\n"])));
// Elements injected before/after the children
var BeforeAfterBase = styled.span(templateObject_2$1 || (templateObject_2$1 = __makeTemplateObject(["\n  align-items: center;\n  display: flex;\n  flex-shrink: 0;\n  /* Fix windows line-height issue */\n  padding-bottom: 1px;\n"], ["\n  align-items: center;\n  display: flex;\n  flex-shrink: 0;\n  /* Fix windows line-height issue */\n  padding-bottom: 1px;\n"])));
var getBeforeSpacing = function (_a) {
    var isCompact = _a.isCompact, theme = _a.theme;
    var spaceKey = isCompact ? 'compact' : 'default';
    var space = getThemeStyle(theme[themeNamespace], spaceKey, 'beforeItemSpacing');
    return css(templateObject_3$1 || (templateObject_3$1 = __makeTemplateObject(["\n    margin-right: ", "px;\n  "], ["\n    margin-right: ", "px;\n  "])), space);
};
var Before = styled(BeforeAfterBase)(templateObject_4$1 || (templateObject_4$1 = __makeTemplateObject(["\n  ", ";\n"], ["\n  ", ";\n"])), getBeforeSpacing);
var getAfterSpacing = function (_a) {
    var isCompact = _a.isCompact, theme = _a.theme;
    var spaceKey = isCompact ? 'compact' : 'default';
    var space = getThemeStyle(theme[themeNamespace], spaceKey, 'afterItemSpacing');
    return css(templateObject_5$1 || (templateObject_5$1 = __makeTemplateObject(["\n    margin-left: ", "px;\n  "], ["\n    margin-left: ", "px;\n  "])), space);
};
var After = styled(BeforeAfterBase)(templateObject_6$1 || (templateObject_6$1 = __makeTemplateObject(["\n  ", ";\n"], ["\n  ", ";\n"])), getAfterSpacing);
// Alignment and layout for the children
var ContentWrapper = styled.span(templateObject_7$1 || (templateObject_7$1 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  flex-grow: 1;\n  margin: 0;\n  overflow: hidden;\n\n  &:first-child {\n    margin: 0;\n  }\n"], ["\n  display: flex;\n  flex-direction: column;\n  flex-grow: 1;\n  margin: 0;\n  overflow: hidden;\n\n  &:first-child {\n    margin: 0;\n  }\n"])));
var Content = styled.span(templateObject_8$1 || (templateObject_8$1 = __makeTemplateObject(["\n  display: block;\n  flex: 1 1 auto;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: ", ";\n  line-height: ", ";\n  /* Fix windows line-height issue */\n  padding-bottom: 1px;\n"], ["\n  display: block;\n  flex: 1 1 auto;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: ", ";\n  line-height: ", ";\n  /* Fix windows line-height issue */\n  padding-bottom: 1px;\n"])), function (_a) {
    var allowMultiline = _a.allowMultiline;
    return allowMultiline ? 'normal' : 'nowrap';
}, 16 / fontSize());
var getColorStyle = function (_a) {
    var isDisabled = _a.isDisabled, theme = _a.theme;
    if (isDisabled) {
        return css(templateObject_9$1 || (templateObject_9$1 = __makeTemplateObject(["\n      color: ", ";\n    "], ["\n      color: ",
            ";\n    "])), getThemeStyle(theme[themeNamespace], 'secondaryText', 'disabled'));
    }
    return css(templateObject_10$1 || (templateObject_10$1 = __makeTemplateObject(["\n    color: ", ";\n\n    /* This detects hover on the grandparent. Saves us having to maintain isHovered\n       state in the grandparent. */\n    *:hover > * > & {\n      color: ", ";\n    }\n\n    *:active > * > & {\n      color: ", ";\n    }\n  "], ["\n    color: ", ";\n\n    /* This detects hover on the grandparent. Saves us having to maintain isHovered\n       state in the grandparent. */\n    *:hover > * > & {\n      color: ",
        ";\n    }\n\n    *:active > * > & {\n      color: ",
        ";\n    }\n  "])), getThemeStyle(theme[themeNamespace], 'secondaryText', 'default'), function () {
        return getThemeStyle(theme[themeNamespace], 'secondaryText', 'hover');
    }, function () {
        return getThemeStyle(theme[themeNamespace], 'secondaryText', 'active');
    });
};
var getDescriptionFontStyles = function (_a) {
    var isCompact = _a.isCompact;
    var descriptionFontSize = isCompact
        ? compactSmallFontSize
        : fontSizeSmall();
    var lineHeight = isCompact ? compactLineHeight : 14 / descriptionFontSize;
    return css(templateObject_11$1 || (templateObject_11$1 = __makeTemplateObject(["\n    font-size: ", "px;\n    line-height: ", ";\n    /* Fix windows line-height issue */\n    padding-bottom: 1px;\n  "], ["\n    font-size: ", "px;\n    line-height: ", ";\n    /* Fix windows line-height issue */\n    padding-bottom: 1px;\n  "])), descriptionFontSize, lineHeight);
};
// Description is a block element below the children, like a subtitle
var Description = styled.span(templateObject_12$1 || (templateObject_12$1 = __makeTemplateObject(["\n  flex: 1 1 auto;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n  ", ";\n  ", ";\n"], ["\n  flex: 1 1 auto;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n  ", ";\n  ", ";\n"])), getColorStyle, getDescriptionFontStyles);
var templateObject_1$3, templateObject_2$1, templateObject_3$1, templateObject_4$1, templateObject_5$1, templateObject_6$1, templateObject_7$1, templateObject_8$1, templateObject_9$1, templateObject_10$1, templateObject_11$1, templateObject_12$1;

var Item = /** @class */ (function (_super) {
    __extends(Item, _super);
    function Item(props) {
        var _this = _super.call(this, props) || this;
        _this.setRef = function (ref) {
            _this.ref = ref;
        };
        _this.href = function () { return (_this.props.isDisabled ? null : _this.props.href); };
        // The type of element rendered at the root of render() can vary based on the `href`
        // and `linkComponent` props provided. We generate this component here to avoid re-
        // generating the component inside render(). This is for performance reasons, and also
        // allows us to avoid generating a new `ref` for the root element each render().
        _this.rootComponent = styledRootElement({
            href: _this.href(),
            linkComponent: props.linkComponent,
        });
        _this.ref = React.createRef();
        return _this;
    }
    Item.prototype.componentDidMount = function () {
        if (this.ref && this.props.autoFocus) {
            //@ts-ignore
            this.ref.focus();
        }
    };
    Item.prototype.render = function () {
        var _this = this;
        var _a = this.props, onClick = _a.onClick, onKeyDown = _a.onKeyDown, isCompact = _a.isCompact, isDisabled = _a.isDisabled, isDragging = _a.isDragging, isHidden = _a.isHidden, isSelected = _a.isSelected, onMouseEnter = _a.onMouseEnter, onMouseLeave = _a.onMouseLeave, role = _a.role, dnd = _a.dnd, otherProps = __rest(_a, ["onClick", "onKeyDown", "isCompact", "isDisabled", "isDragging", "isHidden", "isSelected", "onMouseEnter", "onMouseLeave", "role", "dnd"]);
        var Root = this.rootComponent;
        var dragHandleProps = (dnd && dnd.dragHandleProps) || null;
        var patchedEventHandlers = {
            onClick: function (event) {
                // rbd will use event.preventDefault() to block clicks that are used
                // as a part of the drag and drop lifecycle.
                if (event.defaultPrevented) {
                    return;
                }
                if (!isDisabled && onClick) {
                    onClick(event);
                }
            },
            onMouseDown: function (event) {
                // rbd 11.x support
                if (dragHandleProps && dragHandleProps.onMouseDown) {
                    dragHandleProps.onMouseDown(event);
                }
                // We want to prevent the item from getting focus when clicked
                event.preventDefault();
            },
            onKeyDown: function (event) {
                // swallowing keyboard events on the element while dragging
                // rbd should already be doing this - but we are being really clear here
                if (isDragging) {
                    return;
                }
                // rbd 11.x support
                if (dragHandleProps && dragHandleProps.onKeyDown) {
                    dragHandleProps.onKeyDown(event);
                }
                // if default is prevented - do not fire other handlers
                // this can happen if the event is used for drag and drop by rbd
                if (event.defaultPrevented) {
                    return;
                }
                // swallowing event if disabled
                if (isDisabled) {
                    return;
                }
                if (!onKeyDown) {
                    return;
                }
                onKeyDown(event);
            },
        };
        var patchedInnerRef = function (ref) {
            _this.setRef(ref);
            // give rbd the inner ref too
            if (dnd && dnd.ref) {
                dnd.ref(ref);
            }
        };
        return (React.createElement(Root, __assign({ "aria-disabled": isDisabled, href: this.href(), isCompact: isCompact, isDisabled: isDisabled, isDragging: isDragging, isHidden: isHidden, isSelected: isSelected, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, role: role, tabIndex: isDisabled || isHidden || this.props.href ? null : 0, target: this.props.target, title: this.props.title, ref: patchedInnerRef }, (dnd && dnd.draggableProps), dragHandleProps, patchedEventHandlers, otherProps),
            !!this.props.elemBefore && (React.createElement(Before, { isCompact: isCompact }, this.props.elemBefore)),
            React.createElement(ContentWrapper, null,
                React.createElement(Content, { allowMultiline: this.props.shouldAllowMultiline }, this.props.children),
                !!this.props.description && (React.createElement(Description, { isCompact: this.props.isCompact, isDisabled: this.props.isDisabled }, this.props.description))),
            !!this.props.elemAfter && (React.createElement(After, { isCompact: isCompact }, this.props.elemAfter))));
    };
    Item.defaultProps = {
        autoFocus: false,
        description: '',
        isCompact: false,
        isDisabled: false,
        isHidden: false,
        role: 'button',
        shouldAllowMultiline: false,
    };
    return Item;
}(React.Component));

var getPadding$1 = function (_a) {
    var isCompact = _a.isCompact, theme = _a.theme;
    var paddingType = isCompact ? 'compact' : 'default';
    var _b = getThemeStyle(theme[themeNamespace], paddingType, 'padding'), _c = _b.bottom, bottom = _c === void 0 ? 0 : _c, _d = _b.left, left = _d === void 0 ? 0 : _d, _e = _b.right, right = _e === void 0 ? 0 : _e, _f = _b.top, top = _f === void 0 ? 0 : _f;
    return css(templateObject_1$4 || (templateObject_1$4 = __makeTemplateObject(["\n    padding: ", "px ", "px ", "px ", "px;\n  "], ["\n    padding: ", "px ", "px ", "px ", "px;\n  "])), top, right, bottom, left);
};
// eslint-disable-next-line import/prefer-default-export
var GroupTitle = styled.div(templateObject_2$2 || (templateObject_2$2 = __makeTemplateObject(["\n  align-items: center;\n  color: ", ";\n  display: flex;\n  flex: 1 1 auto;\n  ", ";\n"], ["\n  align-items: center;\n  color: ",
    ";\n  display: flex;\n  flex: 1 1 auto;\n  ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return getThemeStyle(theme[themeNamespace], 'secondaryText', 'default');
}, getPadding$1);
GroupTitle.displayName = 'ItemGroupTitle';
var GroupTitleAfter = styled.div(templateObject_3$2 || (templateObject_3$2 = __makeTemplateObject(["\n  flex: 0 0 auto;\n  margin-right: -", "px;\n"], ["\n  flex: 0 0 auto;\n  margin-right: -", "px;\n"])), gridSize$1 / 2);
GroupTitleAfter.displayName = 'ItemGroupTitleAfter';
var GroupTitleText = styled.div(templateObject_4$2 || (templateObject_4$2 = __makeTemplateObject(["\n  flex: 1 1 auto;\n  font-size: ", "px;\n  line-height: 1;\n  text-transform: uppercase;\n  /* Required for children to truncate */\n  min-width: 0;\n"], ["\n  flex: 1 1 auto;\n  font-size: ", "px;\n  line-height: 1;\n  text-transform: uppercase;\n  /* Required for children to truncate */\n  min-width: 0;\n"])), fontSizeSmall);
GroupTitleText.displayName = 'ItemGroupTitleText';
var templateObject_1$4, templateObject_2$2, templateObject_3$2, templateObject_4$2;

var ItemGroup = /** @class */ (function (_super) {
    __extends(ItemGroup, _super);
    function ItemGroup() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    ItemGroup.prototype.render = function () {
        var _a;
        var _b = this.props, children = _b.children, elemAfter = _b.elemAfter, isCompact = _b.isCompact, title = _b.title, label = _b.label, innerRef = _b.innerRef, role = _b.role;
        var ariaLabel = (_a = label !== null && label !== void 0 ? label : title) !== null && _a !== void 0 ? _a : '';
        return (React.createElement("div", { "aria-label": ariaLabel.toString(), role: role, ref: innerRef },
            title ? (React.createElement(GroupTitle, { "aria-hidden": "true", isCompact: isCompact },
                React.createElement(GroupTitleText, null, title),
                elemAfter ? (React.createElement(GroupTitleAfter, null, elemAfter)) : null)) : null,
            children));
    };
    ItemGroup.defaultProps = {
        role: 'group',
    };
    return ItemGroup;
}(React.Component));

// import { akEditorMobileMaxWidth } from '@atlaskit/editor-shared-styles';
var akEditorMobileMaxWidth = 0;
// Taken from the style of inline dialog components
var dropShadow = css(templateObject_1$5 || (templateObject_1$5 = __makeTemplateObject(["\n  box-shadow: 0 0 1px rgba(9, 30, 66, 0.31),\n    0 4px 8px -2px rgba(9, 30, 66, 0.25);\n"], ["\n  box-shadow: 0 0 1px rgba(9, 30, 66, 0.31),\n    0 4px 8px -2px rgba(9, 30, 66, 0.25);\n"])));
var Shortcut = styled.div(templateObject_2$3 || (templateObject_2$3 = __makeTemplateObject(["\n  background-color: rgba(223, 225, 229, 0.5); /* N60 at 50% */\n  color: ", ";\n  border-radius: ", "px;\n  padding: 4px;\n  line-height: 12px;\n  font-size: 11.67px;\n  align-self: flex-end;\n  @media (max-width: ", "px) {\n    display: none;\n  }\n"], ["\n  background-color: rgba(223, 225, 229, 0.5); /* N60 at 50% */\n  color: ", ";\n  border-radius: ", "px;\n  padding: 4px;\n  line-height: 12px;\n  font-size: 11.67px;\n  align-self: flex-end;\n  @media (max-width: ", "px) {\n    display: none;\n  }\n"])), N100, borderRadius(), akEditorMobileMaxWidth);
var CellColourPreview = styled.div(templateObject_3$3 || (templateObject_3$3 = __makeTemplateObject(["\n  &::before {\n    background: ", ";\n  }\n"], ["\n  &::before {\n    background: ", ";\n  }\n"])), function (props) { return props.selectedColor; });
var templateObject_1$5, templateObject_2$3, templateObject_3$3;

function IconFallback(_a) {
    var _b = _a.label, label = _b === void 0 ? '' : _b;
    return (React.createElement("svg", { "aria-label": label, width: 40, height: 40 },
        React.createElement("defs", null,
            React.createElement("linearGradient", { x1: "-26.046%", y1: "100%", x2: "62.626%", y2: "0%", id: "fallback-a" },
                React.createElement("stop", { stopColor: "#B3BAC5", offset: "0%" }),
                React.createElement("stop", { stopColor: "#A5ADBA", offset: "100%" }))),
        React.createElement("g", { fill: "none", fillRule: "evenodd" },
            React.createElement("path", { fill: "#FFF", d: "M0 0h40v40H0z" }),
            React.createElement("path", { fill: "#DFE1E6", d: "M20 16h12v12H20z" }),
            React.createElement("path", { fill: "url(#fallback-a)", d: "M8 16h12v12H8z" }),
            React.createElement("path", { d: "M20 16c-4 .5-6.029 2.5-6.086 6-.057 3.5-2.028 5.5-5.914 6h12V16z", fill: "#A5ADBA" }),
            React.createElement("path", { fill: "#B3BAC5", d: "M17.5 13h5v3h-5z" }),
            React.createElement("path", { fill: "#A5ADBA", d: "M10 13h5v3h-5z" }),
            React.createElement("path", { fill: "#DFE1E6", d: "M25 13h5v3h-5z" }))));
}

var _a;
var hidden = { overflow: 'hidden' };
var itemTheme = (_a = {},
    _a[themeNamespace] = {
        padding: {
            default: {
                bottom: 12,
                left: 12,
                right: 12,
                top: 12,
            },
        },
        beforeItemSpacing: {
            default: function () { return 12; },
        },
        borderRadius: function () { return 0; },
        hover: {
            // background: colors.transparent, transparent is not a thing
            text: text$1,
            secondaryText: N200,
        },
        selected: {
            background: N20,
            text: N800,
            secondaryText: N200,
        },
    },
    _a);
var ItemIcon = styled.div(templateObject_1$6 || (templateObject_1$6 = __makeTemplateObject(["\n  width: 40px;\n  height: 40px;\n  overflow: hidden;\n  border: 1px solid rgba(223, 225, 229, 0.5); /* N60 at 50% */\n  border-radius: ", "px;\n  box-sizing: border-box;\n\n  display: flex;\n  justify-content: center;\n  align-items: center;\n\n  div {\n    width: 40px;\n    height: 40px;\n  }\n"], ["\n  width: 40px;\n  height: 40px;\n  overflow: hidden;\n  border: 1px solid rgba(223, 225, 229, 0.5); /* N60 at 50% */\n  border-radius: ", "px;\n  box-sizing: border-box;\n\n  display: flex;\n  justify-content: center;\n  align-items: center;\n\n  div {\n    width: 40px;\n    height: 40px;\n  }\n"])), borderRadius());
var ItemBody = styled.div(templateObject_2$4 || (templateObject_2$4 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: row;\n  flex-wrap: nowrap;\n  justify-content: space-between;\n  line-height: 1.4;\n"], ["\n  display: flex;\n  flex-direction: row;\n  flex-wrap: nowrap;\n  justify-content: space-between;\n  line-height: 1.4;\n"])));
var ItemText = styled.div(templateObject_3$4 || (templateObject_3$4 = __makeTemplateObject(["\n  white-space: initial;\n  .item-description {\n    font-size: 11.67px;\n    color: ", ";\n    margin-top: 4px;\n  }\n"], ["\n  white-space: initial;\n  .item-description {\n    font-size: 11.67px;\n    color: ", ";\n    margin-top: 4px;\n  }\n"])), N200);
var ItemAfter = styled.div(templateObject_4$3 || (templateObject_4$3 = __makeTemplateObject(["\n  flex: 0 0 auto;\n"], ["\n  flex: 0 0 auto;\n"])));
var fallbackIcon = function (label) {
    return React.createElement(IconFallback, { label: label });
};
function scrollIntoViewIfNeeded(element) {
    var offsetTop = element.offsetTop, offsetHeight = element.offsetHeight, offsetParent = element.offsetParent;
    var _a = offsetParent, offsetParentHeight = _a.offsetHeight, scrollTop = _a.scrollTop;
    var direction = offsetTop + offsetHeight > offsetParentHeight + scrollTop
        ? 1
        : scrollTop > offsetTop
            ? -1
            : 0;
    if (direction !== 0 && offsetParent) {
        offsetParent.scrollTop =
            direction === 1
                ? offsetTop + offsetHeight - offsetParentHeight
                : offsetTop;
    }
}
function TypeAheadItemsList(_a) {
    var items = _a.items, currentIndex = _a.currentIndex, insertByIndex = _a.insertByIndex, setCurrentIndex = _a.setCurrentIndex;
    if (!Array.isArray(items)) {
        return null;
    }
    return (React.createElement(ThemeProvider, { theme: itemTheme },
        React.createElement(ItemGroup, null, items.map(function (item, index) { return (React.createElement(TypeAheadItemComponent, { key: item.key || item.title, item: item, index: index, currentIndex: currentIndex, insertByIndex: insertByIndex, setCurrentIndex: setCurrentIndex })); }))));
}
var TypeAheadItemComponent = /** @class */ (function (_super) {
    __extends(TypeAheadItemComponent, _super);
    function TypeAheadItemComponent(props) {
        var _this = _super.call(this, props) || this;
        _this.insertByIndex = function () {
            _this.props.insertByIndex(_this.props.index);
        };
        _this.setCurrentIndex = function () {
            if (!_this.isSelected(_this.props)) {
                _this.props.setCurrentIndex(_this.props.index);
            }
        };
        _this.handleRef = function (ref) {
            // For some reason, this works quite well without this..
            // let hasRef = (ref: any): ref is { ref: HTMLElement } => ref && ref.ref;
            // this.setState({ ref: hasRef(ref) ? ref.ref : ref });
        };
        _this.ref = React.createRef();
        return _this;
    }
    TypeAheadItemComponent.prototype.shouldComponentUpdate = function (nextProps) {
        return this.isSelected(this.props) !== this.isSelected(nextProps);
    };
    TypeAheadItemComponent.prototype.isSelected = function (props) {
        return props.index === props.currentIndex;
    };
    TypeAheadItemComponent.prototype.componentDidUpdate = function () {
        if (this.props.index === this.props.currentIndex && this.ref && this.ref.current) {
            scrollIntoViewIfNeeded(this.ref.current);
        }
    };
    TypeAheadItemComponent.prototype.render = function () {
        var item = this.props.item;
        return item.render ? (React.createElement("div", { onMouseMove: this.setCurrentIndex, 
            // ref={this.handleRef}
            style: hidden },
            React.createElement(item.render, { onClick: this.insertByIndex, onHover: this.setCurrentIndex, isSelected: this.isSelected(this.props) }))) : (React.createElement(Item, { onClick: this.insertByIndex, onMouseMove: this.setCurrentIndex, elemBefore: React.createElement(ItemIcon, null, item.icon ? item.icon() : fallbackIcon(item.title)), isSelected: this.isSelected(this.props), "aria-describedby": item.title, ref: this.handleRef },
            React.createElement(ItemBody, null,
                React.createElement(ItemText, null,
                    React.createElement("div", { className: "item-title" }, item.title),
                    item.description && (React.createElement("div", { className: "item-description" }, item.description))),
                React.createElement(ItemAfter, null, item.keyshortcut && React.createElement(Shortcut, null, item.keyshortcut)))));
    };
    return TypeAheadItemComponent;
}(React.Component));
var templateObject_1$6, templateObject_2$4, templateObject_3$4, templateObject_4$3;

// import { akEditorFloatingDialogZIndex } from '@atlaskit/editor-shared-styles';
var akEditorFloatingDialogZIndex = 410;
var TypeAheadContent = styled.div(templateObject_1$7 || (templateObject_1$7 = __makeTemplateObject(["\n  background: ", ";\n  border-radius: ", "px;\n  box-shadow: 0 0 1px ", ", 0 4px 8px -2px ", ";\n  padding: ", "px 0;\n  width: 320px;\n  max-height: 380px; /* ~5.5 visibile items */\n  overflow-y: auto;\n  -ms-overflow-style: -ms-autohiding-scrollbar;\n  position: relative;\n"], ["\n  background: ", ";\n  border-radius: ", "px;\n  box-shadow: 0 0 1px ", ", 0 4px 8px -2px ", ";\n  padding: ", "px 0;\n  width: 320px;\n  max-height: 380px; /* ~5.5 visibile items */\n  overflow-y: auto;\n  -ms-overflow-style: -ms-autohiding-scrollbar;\n  position: relative;\n"])), N0, borderRadius(), N60A, N50A, divide(gridSize, 2));
var TypeAhead = /** @class */ (function (_super) {
    __extends(TypeAhead, _super);
    function TypeAhead() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.composing = false;
        _this.handleKeyPress = function () {
            // When user starts typing, they are not using their mouse
            // Marks as composing, to prevent false positive mouse events
            _this.composing = true;
        };
        _this.handleMouseMove = function () {
            // User is actively moving mouse, hence can enable mouse events again
            _this.composing = false;
        };
        _this.componentDidMount = function () {
            window.addEventListener('keypress', _this.handleKeyPress);
            window.addEventListener('mousemove', _this.handleMouseMove);
        };
        _this.componentWillUnmount = function () {
            window.removeEventListener('keypress', _this.handleKeyPress);
            window.removeEventListener('mousemove', _this.handleMouseMove);
        };
        _this.insertByIndex = function (index) {
            selectByIndex(index)(_this.props.editorView.state, _this.props.editorView.dispatch);
        };
        _this.setCurrentIndex = function (index) {
            if (_this.composing) {
                // User is typing, mouse events are false positives
                return;
            }
            if (index !== _this.props.currentIndex) {
                setCurrentIndex(index)(_this.props.editorView.state, _this.props.editorView.dispatch);
            }
        };
        return _this;
    }
    TypeAhead.prototype.render = function () {
        var _a = this.props, active = _a.active, items = _a.items, isLoading = _a.isLoading, anchorElement = _a.anchorElement, currentIndex = _a.currentIndex, popupsMountPoint = _a.popupsMountPoint, popupsBoundariesElement = _a.popupsBoundariesElement, popupsScrollableElement = _a.popupsScrollableElement, highlight = _a.highlight;
        if (!active || !anchorElement || !items || !items.length) {
            return null;
        }
        return (React.createElement(Popup, { zIndex: akEditorFloatingDialogZIndex, target: anchorElement, mountTo: popupsMountPoint, boundariesElement: popupsBoundariesElement, scrollableElement: popupsScrollableElement, fitHeight: 300, fitWidth: 340, offset: [0, 8] },
            React.createElement(TypeAheadContent, { className: "fabric-editor-typeahead" },
                highlight,
                Array.isArray(items) ? (React.createElement(TypeAheadItemsList, { insertByIndex: this.insertByIndex, setCurrentIndex: this.setCurrentIndex, items: items, currentIndex: currentIndex })) : !items && isLoading ? ('loading...') : ('no items'))));
    };
    TypeAhead.displayName = 'TypeAhead';
    return TypeAhead;
}(React.Component));
var templateObject_1$7;

var typeAheadPlugin = function () { return ({
    name: 'typeAhead',
    marks: function () {
        return [{ name: 'typeAheadQuery', mark: typeAheadQuery }];
    },
    pmPlugins: function (typeAhead) {
        if (typeAhead === void 0) { typeAhead = []; }
        return [
            {
                name: 'typeAhead',
                plugin: function (_a) {
                    var dispatch = _a.dispatch;
                    return createPlugin(dispatch, typeAhead);
                },
            },
            {
                name: 'typeAheadInputRule',
                plugin: function (_a) {
                    var schema = _a.schema;
                    return inputRulePlugin(schema, typeAhead);
                },
            },
            {
                name: 'typeAheadKeymap',
                plugin: function () { return keymapPlugin$1(); },
            },
        ];
    },
    contentComponent: function (_a) {
        var editorView = _a.editorView, popupsMountPoint = _a.popupsMountPoint, popupsBoundariesElement = _a.popupsBoundariesElement, popupsScrollableElement = _a.popupsScrollableElement;
        return (React.createElement(WithPluginState, { plugins: {
                typeAhead: pluginKey$1,
            }, render: function (_a) {
                var _b = _a.typeAhead, typeAhead = _b === void 0 ? createInitialPluginState() : _b;
                if (typeAhead.typeAheadHandler &&
                    typeAhead.typeAheadHandler.headless) {
                    return null;
                }
                var queryMarkPos = typeAhead.queryMarkPos;
                var domRef = null;
                if (queryMarkPos !== null) {
                    // temporary fix to avoid page crash until it is fixed properly
                    try {
                        domRef = editorView.domAtPos(queryMarkPos);
                    }
                    catch (ex) {
                        return null;
                    }
                }
                var anchorElement = domRef
                    ? domRef.node.childNodes[domRef.offset]
                    : undefined;
                return (React.createElement(TypeAhead, { editorView: editorView, popupsMountPoint: popupsMountPoint, popupsBoundariesElement: popupsBoundariesElement, popupsScrollableElement: popupsScrollableElement, anchorElement: anchorElement, active: typeAhead.active, isLoading: !!typeAhead.itemsLoader, items: typeAhead.items, currentIndex: typeAhead.currentIndex, highlight: typeAhead.highlight }));
            } }));
    },
}); };

var Preset = /** @class */ (function () {
    function Preset() {
        this.plugins = [];
    }
    Preset.prototype.add = function (plugin) {
        this.plugins.push(plugin);
        return this;
    };
    Preset.prototype.has = function (plugin) {
        return this.plugins.some(function (pluginPreset) {
            if (Array.isArray(pluginPreset)) {
                return pluginPreset[0] === plugin;
            }
            return pluginPreset === plugin;
        });
    };
    Preset.prototype.getEditorPlugins = function (excludes) {
        var editorPlugins = this.processEditorPlugins();
        return this.removeExcludedPlugins(editorPlugins, excludes);
    };
    Preset.prototype.processEditorPlugins = function () {
        var cache = new Map();
        this.plugins.forEach(function (pluginEntry) {
            if (Array.isArray(pluginEntry)) {
                var fn = pluginEntry[0], options = pluginEntry[1];
                cache.set(fn, options);
            }
            else {
                /**
                 * Prevent usage of same plugin twice without override.
                 * [
                 *  plugin1,
                 *  [plugin1, { option1: value }],
                 *  plugin1, // This will throw
                 * ]
                 */
                if (cache.has(pluginEntry) && cache.get(pluginEntry) === undefined) {
                    throw new Error(pluginEntry + " is already included!");
                }
                cache.set(pluginEntry, undefined);
            }
        });
        var plugins = [];
        cache.forEach(function (options, fn) {
            plugins.push(fn(options));
        });
        return plugins;
    };
    Preset.prototype.removeExcludedPlugins = function (plugins, excludes) {
        if (excludes) {
            return plugins.filter(function (plugin) { return !plugin || !excludes.has(plugin.name); });
        }
        return plugins;
    };
    return Preset;
}());

/**
 * Maps EditorProps to EditorPlugins
 */
function createPluginsList(props, prevProps) {
    var preset = new Preset();
    preset.add(basePlugin);
    preset.add(blockQuotePlugin);
    preset.add(quickInsertPlugin);
    preset.add(typeAheadPlugin);
    return preset.getEditorPlugins();
}

function createSchema(editorConfig) {
    var createdMarks = fixExcludes(editorConfig.marks.sort(sortByOrder('marks')).reduce(function (acc, mark) {
        acc[mark.name] = mark.mark;
        return acc;
    }, {}));
    var createdNodes = editorConfig.nodes.sort(sortByOrder('nodes')).reduce(function (acc, node) {
        acc[node.name] = node.node;
        return acc;
    }, {});
    return new Schema({ nodes: createdNodes, marks: createdMarks });
}

var ReactEditorView = /** @class */ (function (_super) {
    __extends(ReactEditorView, _super);
    function ReactEditorView(props) {
        var _this = _super.call(this, props) || this;
        // ProseMirror is instantiated prior to the initial React render cycle,
        // so we allow transactions by default, to avoid discarding the initial one.
        _this.canDispatchTransactions = true;
        _this.createEditorState = function (options) {
            if (_this.editorView) {
                /**
                 * There's presently a number of issues with changing the schema of a
                 * editor inflight. A significant issue is that we lose the ability
                 * to keep track of a user's history as the internal plugin state
                 * keeps a list of Steps to undo/redo (which are tied to the schema).
                 * Without a good way to do work around this, we prevent this for now.
                 */
                // eslint-disable-next-line no-console
                console.warn('The editor does not support changing the schema dynamically.');
                return _this.editorState;
            }
            _this.config = processPluginsList(_this.getPlugins(options.props.editorProps, undefined));
            var schema = createSchema(_this.config);
            var plugins = createPMPlugins({
                schema: schema,
                dispatch: _this.dispatch,
                editorConfig: _this.config,
                eventDispatcher: _this.eventDispatcher,
                providerFactory: options.props.providerFactory,
                portalProviderAPI: _this.props.portalProviderAPI,
            });
            var doc;
            // Workaround for ED-3507: When media node is the last element, scrollIntoView throws an error
            var patchedSelection =  undefined;
            return EditorState.create({
                schema: schema,
                plugins: plugins,
                doc: doc,
                selection: patchedSelection,
            });
        };
        _this.reconfigureState = function (props) {
            if (!_this.editorView) {
                return;
            }
            // We cannot currently guarantee when all the portals will have re-rendered during a reconfigure
            // so we blur here to stop ProseMirror from trying to apply selection to detached nodes or
            // nodes that haven't been re-rendered to the document yet.
            if (_this.editorView.dom instanceof HTMLElement && _this.editorView.hasFocus()) {
                _this.editorView.dom.blur();
            }
            _this.config = processPluginsList(_this.getPlugins(props.editorProps, _this.props.editorProps));
            var state = _this.editorState;
            var plugins = createPMPlugins({
                schema: state.schema,
                dispatch: _this.dispatch,
                editorConfig: _this.config,
                eventDispatcher: _this.eventDispatcher,
                providerFactory: props.providerFactory,
                portalProviderAPI: props.portalProviderAPI,
            });
            var newState = state.reconfigure({ plugins: plugins });
            // need to update the state first so when the view builds the nodeviews it is
            // using the latest plugins
            _this.editorView.updateState(newState);
            return _this.editorView.update(__assign(__assign({}, _this.editorView.props), { state: newState }));
        };
        _this.dispatchTransaction = function (transaction) {
            if (!_this.editorView) {
                return;
            }
             startMeasure("\uD83E\uDD89 ReactEditorView::dispatchTransaction");
            var nodes = findChangedNodesFromTransaction(transaction);
            var changedNodesValid = validateNodes(nodes);
            if (changedNodesValid) {
                var oldEditorState = _this.editorView.state;
                // go ahead and update the state now we know the transaction is good
                 startMeasure("\uD83E\uDD89 EditorView::state::apply");
                var editorState = _this.editorView.state.apply(transaction);
                 stopMeasure("\uD83E\uDD89 EditorView::state::apply");
                if (editorState === oldEditorState) {
                    return;
                }
                 startMeasure("\uD83E\uDD89 EditorView::updateState");
                _this.editorView.updateState(editorState);
                 stopMeasure("\uD83E\uDD89 EditorView::updateState");
                _this.editorState = editorState;
            }
            else {
                var invalidNodes = nodes
                    .filter(function (node) { return !validNode(node); })
                    .map(function (node) { return getDocStructure(node); });
                {
                    console.error('Invalid nodes in transaction');
                    console.log(transaction);
                    console.log(invalidNodes);
                }
            }
            
                stopMeasure("\uD83E\uDD89 ReactEditorView::dispatchTransaction", function () { });
        };
        _this.getDirectEditorProps = function (state) {
            return {
                state: state || _this.editorState,
                dispatchTransaction: function (tr) {
                    // Block stale transactions:
                    // Prevent runtime exceptions from async transactions that would attempt to
                    // update the DOM after React has unmounted the Editor.
                    if (_this.canDispatchTransactions) {
                        _this.dispatchTransaction(tr);
                    }
                },
                // Disables the contentEditable attribute of the editor if the editor is disabled
                editable: function (_state) { return true; },
                attributes: { 'data-gramm': 'false' },
            };
        };
        _this.handleEditorViewRef = function (node) {
            if (!_this.editorView && node) {
                _this.createEditorView(node);
                var view = _this.editorView;
                _this.props.onEditorCreated({
                    view: view,
                    config: _this.config,
                    eventDispatcher: _this.eventDispatcher,
                });
                if (_this.props.editorProps.shouldFocus) {
                    _this.focusTimeoutId = handleEditorFocus(view);
                }
                // Force React to re-render so consumers get a reference to the editor view
                _this.forceUpdate();
            }
            else if (_this.editorView && !node) {
                // When the appearance is changed, React will call handleEditorViewRef with node === null
                // to destroy the old EditorView, before calling this method again with node === div to
                // create the new EditorView
                _this.props.onEditorDestroyed({
                    view: _this.editorView,
                    config: _this.config,
                    eventDispatcher: _this.eventDispatcher,
                });
                _this.editorView.destroy(); // Destroys the dom node & all node views
                _this.editorView = undefined;
            }
        };
        _this.editor = (React.createElement("div", { className: getUAPrefix(), key: "ProseMirror", ref: _this.handleEditorViewRef }));
        _this.eventDispatcher = new EventDispatcher();
        _this.dispatch = createDispatch(_this.eventDispatcher);
        _this.editorState = _this.createEditorState({
            props: props,
            replaceDoc: true,
        });
        return _this;
    }
    ReactEditorView.prototype.componentDidMount = function () {
        // Transaction dispatching is already enabled by default prior to
        // mounting, but we reset it here, just in case the editor view
        // instance is ever recycled (mounted again after unmounting) with
        // the same key.
        // Although storing mounted state is an anti-pattern in React,
        // we do so here so that we can intercept and abort asynchronous
        // ProseMirror transactions when a dismount is imminent.
        this.canDispatchTransactions = true;
    };
    /**
     * Clean up any non-PM resources when the editor is unmounted
     */
    ReactEditorView.prototype.componentWillUnmount = function () {
        // We can ignore any transactions from this point onwards.
        // This serves to avoid potential runtime exceptions which could arise
        // from an async dispatched transaction after it's unmounted.
        // this.canDispatchTransactions = false;
        this.eventDispatcher.destroy();
        clearTimeout(this.focusTimeoutId);
        if (this.editorView) {
            // Destroy the state if the Editor is being unmounted
            var editorState_1 = this.editorView.state;
            editorState_1.plugins.forEach(function (plugin) {
                var state = plugin.getState(editorState_1);
                if (state && state.destroy) {
                    state.destroy();
                }
            });
        }
        // this.editorView will be destroyed when React unmounts in handleEditorViewRef
    };
    // Helper to allow tests to inject plugins directly
    ReactEditorView.prototype.getPlugins = function (editorProps, prevEditorProps) {
        return createPluginsList();
    };
    ReactEditorView.prototype.createEditorView = function (element) {
        // Creates the editor-view from this.editorState. If an editor has been mounted
        // previously, this will contain the previous state of the editor.
        this.editorView = new EditorView({ mount: element }, this.getDirectEditorProps());
        applyDevTools(this.editorView);
    };
    ReactEditorView.prototype.render = function () {
        return this.props.render
            ? this.props.render({
                editor: this.editor,
                view: this.editorView,
                config: this.config,
                eventDispatcher: this.eventDispatcher,
            })
            : this.editor;
    };
    return ReactEditorView;
}(React.Component));
function handleEditorFocus(view) {
    if (view.hasFocus()) {
        return;
    }
    return window.setTimeout(function () {
        view.focus();
    }, 0);
}

var EditorActions = /** @class */ (function () {
    function EditorActions() {
        this.listeners = [];
    }
    EditorActions.from = function (view, eventDispatcher, transformer) {
        var editorActions = new EditorActions();
        editorActions._privateRegisterEditor(view, eventDispatcher, transformer);
        return editorActions;
    };
    //#region private
    // This method needs to be public for context based helper components.
    EditorActions.prototype._privateGetEditorView = function () {
        return this.editorView;
    };
    EditorActions.prototype._privateGetEventDispatcher = function () {
        return this.eventDispatcher;
    };
    // This method needs to be public for EditorContext component.
    EditorActions.prototype._privateRegisterEditor = function (editorView, eventDispatcher, contentTransformer) {
        this.contentTransformer = contentTransformer;
        this.eventDispatcher = eventDispatcher;
        if (!this.editorView && editorView) {
            this.editorView = editorView;
            this.listeners.forEach(function (cb) { return cb(editorView, eventDispatcher); });
        }
        else if (this.editorView !== editorView) {
            throw new Error("Editor has already been registered! It's not allowed to re-register editor with the new Editor instance.");
        }
        if (this.contentTransformer) {
            this.contentEncode = this.contentTransformer.encode.bind(this.contentTransformer);
        }
    };
    // This method needs to be public for EditorContext component.
    EditorActions.prototype._privateUnregisterEditor = function () {
        this.editorView = undefined;
        this.contentTransformer = undefined;
        this.contentEncode = undefined;
        this.eventDispatcher = undefined;
    };
    EditorActions.prototype._privateSubscribe = function (cb) {
        // If editor is registered and somebody is trying to add a listener,
        // just call it first.
        if (this.editorView && this.eventDispatcher) {
            cb(this.editorView, this.eventDispatcher);
        }
        this.listeners.push(cb);
    };
    EditorActions.prototype._privateUnsubscribe = function (cb) {
        this.listeners = this.listeners.filter(function (c) { return c !== cb; });
    };
    //#endregion
    EditorActions.prototype.focus = function () {
        if (!this.editorView || this.editorView.hasFocus()) {
            return false;
        }
        this.editorView.focus();
        this.editorView.dispatch(this.editorView.state.tr.scrollIntoView());
        return true;
    };
    EditorActions.prototype.blur = function () {
        if (!this.editorView || !this.editorView.hasFocus()) {
            return false;
        }
        this.editorView.dom.blur();
        return true;
    };
    EditorActions.prototype.clear = function () {
        if (!this.editorView) {
            return false;
        }
        var editorView = this.editorView;
        var state = editorView.state;
        var tr = editorView.state.tr
            .setSelection(TextSelection.create(state.doc, 0, state.doc.nodeSize - 2))
            .deleteSelection();
        editorView.dispatch(tr);
        return true;
    };
    // async __temporaryFixForConfigPanel() {
    //   const { editorView } = this;
    //   if (!editorView) {
    //     return;
    //   }
    //   __temporaryFixForConfigPanel(editorView);
    // }
    EditorActions.prototype.appendText = function (text) {
        if (!this.editorView || !text) {
            return false;
        }
        var state = this.editorView.state;
        var lastChild = state.doc.lastChild;
        if (lastChild && lastChild.type !== state.schema.nodes.paragraph) {
            return false;
        }
        var tr = state.tr.insertText(text).scrollIntoView();
        this.editorView.dispatch(tr);
        return true;
    };
    return EditorActions;
}());

var EditorContext = /** @class */ (function (_super) {
    __extends(EditorContext, _super);
    function EditorContext(props) {
        var _this = _super.call(this, props) || this;
        _this.editorActions = props.editorActions || new EditorActions();
        return _this;
    }
    EditorContext.prototype.getChildContext = function () {
        return {
            editorActions: this.editorActions,
        };
    };
    EditorContext.prototype.render = function () {
        return React.Children.only(this.props.children);
    };
    EditorContext.childContextTypes = {
        editorActions: PropTypes.object,
    };
    return EditorContext;
}(React.Component));

var PortalProviderAPI = /** @class */ (function (_super) {
    __extends(PortalProviderAPI, _super);
    function PortalProviderAPI() {
        var _this = _super.call(this) || this;
        _this.portals = new Map();
        _this.setContext = function (context) {
            _this.context = context;
        };
        return _this;
    }
    PortalProviderAPI.prototype.render = function (children, container, hasReactContext) {
        if (hasReactContext === void 0) { hasReactContext = false; }
        this.portals.set(container, { children: children, hasReactContext: hasReactContext });
        var wrappedChildren = children();
        unstable_renderSubtreeIntoContainer(this.context, wrappedChildren, container);
    };
    // TODO: until https://product-fabric.atlassian.net/browse/ED-5013
    // we (unfortunately) need to re-render to pass down any updated context.
    // selectively do this for nodeviews that opt-in via `hasReactContext`
    PortalProviderAPI.prototype.forceUpdate = function () {
        var _this = this;
        this.portals.forEach(function (portal, container) {
            if (!portal.hasReactContext) {
                return;
            }
            var wrappedChildren = portal.children();
            unstable_renderSubtreeIntoContainer(_this.context, wrappedChildren, container);
        });
    };
    PortalProviderAPI.prototype.remove = function (container) {
        this.portals.delete(container);
        // There is a race condition that can happen caused by Prosemirror vs React,
        // where Prosemirror removes the container from the DOM before React gets
        // around to removing the child from the container
        // This will throw a NotFoundError: The node to be removed is not a child of this node
        // Both Prosemirror and React remove the elements asynchronously, and in edge
        // cases Prosemirror beats React
        try {
            unmountComponentAtNode(container);
        }
        catch (error) {
            console.warn('Race condition in PortalProviderAPI where PM removed DOM node before React unmounted it');
        }
    };
    return PortalProviderAPI;
}(EventDispatcher));

var PortalProvider = /** @class */ (function (_super) {
    __extends(PortalProvider, _super);
    function PortalProvider(props) {
        var _this = _super.call(this, props) || this;
        _this.portalProviderAPI = new PortalProviderAPI();
        return _this;
    }
    PortalProvider.prototype.render = function () {
        return this.props.render(this.portalProviderAPI);
    };
    PortalProvider.prototype.componentDidUpdate = function () {
        this.portalProviderAPI.forceUpdate();
    };
    PortalProvider.displayName = 'PortalProvider';
    return PortalProvider;
}(React.Component));

var PortalRenderer = /** @class */ (function (_super) {
    __extends(PortalRenderer, _super);
    function PortalRenderer(props) {
        var _this = _super.call(this, props) || this;
        _this.handleUpdate = function (portals) { return _this.setState({ portals: portals }); };
        props.portalProviderAPI.setContext(_this);
        props.portalProviderAPI.on('update', _this.handleUpdate);
        _this.state = { portals: new Map() };
        return _this;
    }
    PortalRenderer.prototype.render = function () {
        var portals = this.state.portals;
        return (React.createElement(React.Fragment, null, Array.from(portals.entries()).map(function (_a) {
            var container = _a[0], children = _a[1];
            return createPortal(children, container);
        })));
    };
    return PortalRenderer;
}(React.Component));

function isUndefined(x) {
    return x === undefined;
}
var ProviderFactory = /** @class */ (function () {
    function ProviderFactory() {
        this.providers = new Map();
        this.subscribers = new Map();
    }
    ProviderFactory.create = function (providers) {
        var providerFactory = new ProviderFactory();
        var keys = Object.keys(providers);
        keys.forEach(function (name) {
            providerFactory.setProvider(name, providers[name]);
        });
        return providerFactory;
    };
    ProviderFactory.prototype.destroy = function () {
        this.providers.clear();
        this.subscribers.clear();
    };
    ProviderFactory.prototype.isEmpty = function () {
        return !this.providers.size && !this.subscribers.size;
    };
    ProviderFactory.prototype.setProvider = function (name, provider) {
        // Do not trigger notifyUpdate if provider is the same.
        if (this.providers.get(name) === provider) {
            return;
        }
        if (!isUndefined(provider)) {
            this.providers.set(name, provider);
        }
        else {
            this.providers.delete(name);
        }
        this.notifyUpdated(name, provider);
    };
    ProviderFactory.prototype.removeProvider = function (name) {
        this.providers.delete(name);
        this.notifyUpdated(name);
    };
    ProviderFactory.prototype.subscribe = function (name, handler) {
        var handlers = this.subscribers.get(name) || [];
        handlers.push(handler);
        this.subscribers.set(name, handlers);
        var provider = this.providers.get(name);
        if (provider) {
            handler(name, provider);
        }
    };
    ProviderFactory.prototype.unsubscribe = function (name, handler) {
        var handlers = this.subscribers.get(name);
        if (!handlers) {
            return;
        }
        var index = handlers.indexOf(handler);
        if (index !== -1) {
            handlers.splice(index, 1);
        }
        if (handlers.length === 0) {
            this.subscribers.delete(name);
        }
        else {
            this.subscribers.set(name, handlers);
        }
    };
    ProviderFactory.prototype.unsubscribeAll = function (name) {
        var handlers = this.subscribers.get(name);
        if (!handlers) {
            return;
        }
        this.subscribers.delete(name);
    };
    ProviderFactory.prototype.hasProvider = function (name) {
        return this.providers.has(name);
    };
    ProviderFactory.prototype.notifyUpdated = function (name, provider) {
        var handlers = this.subscribers.get(name);
        if (!handlers) {
            return;
        }
        handlers.forEach(function (handler) {
            handler(name, provider);
        });
    };
    return ProviderFactory;
}());

/*
 * From Modernizr
 * Returns the kind of transitionevent available for the element
 */
function whichTransitionEvent() {
    var el = document.createElement('fakeelement');
    var transitions = {
        transition: 'transitionend',
        MozTransition: 'transitionend',
        OTransition: 'oTransitionEnd',
        WebkitTransition: 'webkitTransitionEnd',
    };
    for (var t in transitions) {
        if (el.style[t] !== undefined) {
            // Use a generic as the return type because TypeScript doesnt know
            // about cross browser features, so we cast here to align to the
            // standard Event spec and propagate the type properly to the callbacks
            // of `addEventListener` and `removeEventListener`.
            return transitions[t];
        }
    }
    return;
}

var PluginsComponentsWrapper = styled.div(templateObject_1$8 || (templateObject_1$8 = __makeTemplateObject(["\n  display: flex;\n"], ["\n  display: flex;\n"])));
var PluginSlot = /** @class */ (function (_super) {
    __extends(PluginSlot, _super);
    function PluginSlot() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.transitionEvent = whichTransitionEvent();
        _this.forceComponentUpdate = function (event) {
            // Only trigger an update if the transition is on a property containing `width`
            // This will cater for media and the content area itself currently.
            if (event.propertyName.includes('width')) {
                _this.forceUpdate();
            }
        };
        _this.removeModeChangeListener = function (contentAreaRef) {
            if (contentAreaRef && _this.transitionEvent) {
                contentAreaRef.removeEventListener(_this.transitionEvent, _this.forceComponentUpdate);
            }
        };
        _this.addModeChangeListener = function (contentAreaRef) {
            if (contentAreaRef && _this.transitionEvent) {
                /**
                 * Update the plugin components once the transition
                 * to full width / default mode completes
                 */
                contentAreaRef.addEventListener(_this.transitionEvent, _this.forceComponentUpdate);
            }
        };
        return _this;
    }
    PluginSlot.prototype.shouldComponentUpdate = function (nextProps) {
        var _a = this.props, editorView = _a.editorView, editorActions = _a.editorActions, items = _a.items, providerFactory = _a.providerFactory, eventDispatcher = _a.eventDispatcher, popupsMountPoint = _a.popupsMountPoint, popupsBoundariesElement = _a.popupsBoundariesElement, popupsScrollableElement = _a.popupsScrollableElement, containerElement = _a.containerElement, disabled = _a.disabled;
        return !(nextProps.editorView === editorView &&
            nextProps.editorActions === editorActions &&
            nextProps.items === items &&
            nextProps.providerFactory === providerFactory &&
            nextProps.eventDispatcher === eventDispatcher &&
            nextProps.popupsMountPoint === popupsMountPoint &&
            nextProps.popupsBoundariesElement === popupsBoundariesElement &&
            nextProps.popupsScrollableElement === popupsScrollableElement &&
            nextProps.containerElement === containerElement &&
            nextProps.disabled === disabled);
    };
    PluginSlot.prototype.componentDidMount = function () {
        this.addModeChangeListener(this.props.contentAreaRef);
    };
    PluginSlot.prototype.UNSAFE_componentWillReceiveProps = function (nextProps) {
        if (this.props.contentAreaRef !== nextProps.contentAreaRef) {
            this.removeModeChangeListener(this.props.contentAreaRef);
            this.addModeChangeListener(nextProps.contentAreaRef);
        }
    };
    PluginSlot.prototype.componentWillUnmount = function () {
        this.removeModeChangeListener(this.props.contentAreaRef);
    };
    PluginSlot.prototype.render = function () {
        var _a = this.props, items = _a.items, editorView = _a.editorView, editorActions = _a.editorActions, eventDispatcher = _a.eventDispatcher, providerFactory = _a.providerFactory, appearance = _a.appearance, popupsMountPoint = _a.popupsMountPoint, popupsBoundariesElement = _a.popupsBoundariesElement, popupsScrollableElement = _a.popupsScrollableElement, containerElement = _a.containerElement, disabled = _a.disabled;
        if (!items || !editorView) {
            return null;
        }
        return (React.createElement(PluginsComponentsWrapper, null, items.map(function (component, key) {
            var props = { key: key };
            var element = component({
                editorView: editorView,
                editorActions: editorActions,
                eventDispatcher: eventDispatcher,
                providerFactory: providerFactory,
                appearance: appearance,
                popupsMountPoint: popupsMountPoint || undefined,
                popupsBoundariesElement: popupsBoundariesElement,
                popupsScrollableElement: popupsScrollableElement,
                containerElement: containerElement,
                disabled: disabled,
            });
            return element && React.cloneElement(element, props);
        })));
    };
    PluginSlot.displayName = 'PluginSlot';
    return PluginSlot;
}(React.Component));
var templateObject_1$8;

function Toolbar(props) {
    // Iterate over primaryToolbarComponents here
    return (React.createElement(Container, null,
        React.createElement(TopRow, null)));
}
var Container = styled.div(templateObject_1$9 || (templateObject_1$9 = __makeTemplateObject(["\n  background: snow;\n  border: 1px solid black;\n  border-bottom: 0;\n  padding: 10px;\n  & > h2 {\n    margin: 0 0 5px 0;\n  }\n"], ["\n  background: snow;\n  border: 1px solid black;\n  border-bottom: 0;\n  padding: 10px;\n  & > h2 {\n    margin: 0 0 5px 0;\n  }\n"])));
var TopRow = styled.div(templateObject_2$5 || (templateObject_2$5 = __makeTemplateObject(["\n  display: flex;\n"], ["\n  display: flex;\n"])));
var templateObject_1$9, templateObject_2$5;

function FullPage(props) {
    var className = props.className, editorDOMElement = props.editorDOMElement;
    var popupsRef = useRef(null);
    var contentAreaRef = useRef(null);
    return (React.createElement(Container$1, { className: className + " popups-wrapper" },
        React.createElement(Popups, { className: "popups", ref: popupsRef }),
        React.createElement(Toolbar, null),
        React.createElement(ContentArea, { ref: contentAreaRef },
            React.createElement(PluginSlot, { editorView: props.editorView, editorActions: props.editorActions, eventDispatcher: props.eventDispatcher, providerFactory: props.providerFactory, appearance: props.appearance, items: props.contentComponents, contentAreaRef: contentAreaRef.current, popupsMountPoint: popupsRef.current, 
                // popupsBoundariesElement={props.popupsBoundariesElement}
                // popupsScrollableElement={props.popupsScrollableElement}
                disabled: false, containerElement: null }),
            editorDOMElement)));
}
var Container$1 = styled.div(templateObject_1$a || (templateObject_1$a = __makeTemplateObject(["\n  position: relative;\n"], ["\n  position: relative;\n"])));
var Popups = styled.div(templateObject_2$6 || (templateObject_2$6 = __makeTemplateObject(["\n  z-index: 9999;\n"], ["\n  z-index: 9999;\n"])));
var ContentArea = styled.div(templateObject_3$5 || (templateObject_3$5 = __makeTemplateObject(["\n  border: 1px solid black;\n  & > .ProseMirror {\n    min-height: 140px;\n    overflow-wrap: break-word;\n    outline: none;\n    padding: 10px;\n    white-space: pre-wrap;\n  }\n"], ["\n  border: 1px solid black;\n  & > .ProseMirror {\n    min-height: 140px;\n    overflow-wrap: break-word;\n    outline: none;\n    padding: 10px;\n    white-space: pre-wrap;\n  }\n"])));
var templateObject_1$a, templateObject_2$6, templateObject_3$5;

function quickInsertProviderFactory() {
    return {
        getItems: function () {
            return new Promise(function (resolve) {
                window.setTimeout(function () { return resolve(quickInsertItems); }, 1000);
            });
        },
    };
}
var quickInsertItems = [
    {
        title: 'Minimum width extension',
        icon: function () { return React.createElement(DevIcon, { label: "dev" }); },
        action: function (insert) {
            return insert({
                type: 'extension',
                attrs: {
                    extensionType: 'com.atlassian.confluence.macro.core',
                    extensionKey: 'block-layout-eh',
                    text: 'Minimum width block extension demo',
                    parameters: {
                        style: { minWidth: 400 },
                    },
                },
            });
        },
    },
    {
        title: 'Lorem ipsum',
        icon: function () { return React.createElement(DevIcon, { label: "dev" }); },
        action: function (insert) {
            return insert({
                type: 'paragraph',
                content: [],
            }).insertText('Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean mi nisl, venenatis eget auctor vitae, venenatis quis lorem. Suspendisse maximus tortor vel dui tincidunt cursus. Vestibulum magna nibh, auctor non auctor id, finibus vitae orci. Nulla viverra ipsum et nunc fringilla ultricies. Pellentesque vitae felis molestie justo finibus accumsan. Suspendisse potenti. Nulla facilisi. Integer dignissim quis velit quis elementum. Sed sit amet varius ante. Duis vestibulum porta augue eu laoreet. Morbi id risus et augue sollicitudin aliquam. In et ligula dolor. Nam ac aliquet diam.');
        },
    },
];

// An interesting feature whose purpose I'm not completely sure what is
// https://bitbucket.org/atlassian/atlassian-frontend-mirror/src/master/editor/editor-common/src/extensions/
// extensionProvider: any
var components = {
    'full-page': FullPage,
};
function Editor(props) {
    var _a = props.appearance, appearance = _a === void 0 ? 'full-page' : _a, quickInsert = props.quickInsert;
    var editorActions = useState(new EditorActions())[0];
    var providerFactory = useState(new ProviderFactory())[0];
    var quickInsertProvider = useState(Promise.resolve(quickInsertProviderFactory()))[0];
    var Component = useMemo(function () { return components[appearance]; }, [appearance]);
    useEffect(function () {
        handleProviders();
    }, []);
    function handleProviders() {
        if (quickInsertProvider) {
            providerFactory.setProvider('quickInsertProvider', quickInsertProvider);
        }
    }
    function onEditorCreated(instance) {
        editorActions._privateRegisterEditor(instance.view, instance.eventDispatcher);
    }
    function onEditorDestroyed(_instance) {
        editorActions._privateUnregisterEditor();
    }
    return (React.createElement(EditorContext, { editorActions: editorActions },
        React.createElement(PortalProvider, { render: function (portalProviderAPI) { return (React.createElement(React.Fragment, null,
                React.createElement(ReactEditorView, { editorProps: props, providerFactory: providerFactory, portalProviderAPI: portalProviderAPI, onEditorCreated: onEditorCreated, onEditorDestroyed: onEditorDestroyed, render: function (_a) {
                        var editor = _a.editor, view = _a.view, eventDispatcher = _a.eventDispatcher, config = _a.config;
                        return (React.createElement(Component, { appearance: appearance, editorActions: editorActions, editorDOMElement: editor, editorView: view, providerFactory: providerFactory, eventDispatcher: eventDispatcher, contentComponents: config.contentComponents, primaryToolbarComponents: config.primaryToolbarComponents }));
                    } }),
                React.createElement(PortalRenderer, { portalProviderAPI: portalProviderAPI }))); } })));
}

export default Editor;
export { Editor };
